import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Building2, TrendingUp, Users, Users as UsersIcon, FileText, DollarSign, Award, Info,
  Bell, Download, Upload, Plus, Trash2, Edit2, Save, X,
  Target, AlertCircle, CheckCircle, Calendar, Mail, Phone,
  MapPin, Globe, Briefcase, PieChart, BarChart3, Activity,
  FileBarChart, Equal, ArrowUp, ArrowDown, Minus,
  Scale, Wallet, ArrowLeft, ChevronRight, Receipt, Landmark
} from 'lucide-react';
import { groupByAccountingClass, CHARGE_CLASSES, PRODUIT_CLASSES } from '../utils/accountingClassification';
import MonthlyExpensesTable from '../components/MonthlyExpensesTable';
import DocumentsSection from '../components/DocumentsSection';
import GrilleTarifaire from '../components/GrilleTarifaire';
import InvestmentTables from '../components/InvestmentTables';
import BilanPanel from '../components/BilanPanel';
import CompteBancairePanel from '../components/CompteBancairePanel';
import DateRangeFilter, { applyDateRange, formatDateFR } from '../components/DateRangeFilter';
import SearchBox, { applySearch } from '../components/SearchBox';
import InvestisseursPanel from '../components/InvestisseursPanel';
import ObjectifsTab from '../components/ObjectifsTab';
import DatePickerField from '../components/DatePickerField';
import CategorySelect from '../components/CategorySelect';
import PeriodFilter, { applyPeriodFilter } from '../components/PeriodFilter';
import DownloadFinancePdfButton from '../components/DownloadFinancePdfButton';
import Revenus from './Revenus';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import EntrepriseOverview from '../components/EntrepriseOverview';
import EditCompanyInfoSheet from '../components/EditCompanyInfoSheet';
import { toast } from 'sonner';

// Catégories comptables (SYSCOHADA / SESAM)
const CHARGE_CATEGORIES = [
  'Achat panneaux & matériel',
  'Transport & douanes',
  'Loyer et charges',
  'Maintenance panneaux',
  'Carburant & véhicules',
  'Salaires',
  'Charges sociales (CNSS)',
  'Mairie / Autorisations',
  'Assurances',
  'Frais bancaires',
  'Honoraires (comptable, avocat)',
  'Télécom & internet',
  'Marketing & publicité',
  'Fournitures de bureau',
  'Formation',
  'Dividendes investisseurs (20%)',
  'Amortissements',
  'Frais financiers',
  'Impôts et taxes',
  'Divers',
];

const PRODUIT_CATEGORIES = [
  'Location panneau (mensuel)',
  'Location panneau (trimestriel)',
  'Location panneau (annuel)',
  'Installation & pose',
  'Impression bâche',
  'Conception graphique',
  'Frais de dossier',
  'Frais de retrait',
  'Refacturation taxes mairie',
  'Indemnités assurance',
  'Cession actif',
  'Produits financiers',
  'Divers',
];

export default function EntrepriseV2({ user, onLogout }) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabFromUrl = searchParams.get('tab');
  const subFromUrl = searchParams.get('sub'); // sous-vue compta (cr|bilan)
  const [activeTab, setActiveTab] = useState(tabFromUrl || 'overview');
  const [loading, setLoading] = useState(true);
  const [comptaView, setComptaView] = useState(subFromUrl || 'menu'); // 'menu' | 'cr' | 'bilan'

  // Sync URL → state lorsqu'on navigue via le navigateur (back/forward) ou query param
  useEffect(() => {
    if (tabFromUrl && tabFromUrl !== activeTab) setActiveTab(tabFromUrl);
    if (subFromUrl && subFromUrl !== comptaView) setComptaView(subFromUrl);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabFromUrl, subFromUrl]);
  
  // Company info
  const [companyInfo, setCompanyInfo] = useState(null);
  const [showEditCompany, setShowEditCompany] = useState(false);
  
  // Analytics & KPIs
  const [analytics, setAnalytics] = useState(null);
  const [alerts, setAlerts] = useState([]);
  
  // Objectives
  const [objectives, setObjectives] = useState([]);
  const [showObjectiveModal, setShowObjectiveModal] = useState(false);
  const [newObjective, setNewObjective] = useState({
    title: '',
    description: '',
    target_value: 0,
    current_value: 0,
    unit: 'FCFA',
    period: 'mensuel',
    deadline: ''
  });
  
  // Employees
  const [employees, setEmployees] = useState([]);
  const [showEmployeeModal, setShowEmployeeModal] = useState(false);
  const [editingEmployeeId, setEditingEmployeeId] = useState(null);
  const [newEmployee, setNewEmployee] = useState({
    nom: '', prenom: '', poste: '', departement: 'Général', email: '', telephone: ''
  });
  
  // Documents
  const [documents, setDocuments] = useState([]);
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [newDocument, setNewDocument] = useState({
    name: '', category: 'Administratif', file_url: '', description: '', expiry_date: ''
  });
  
  // Expenses
  const [expenses, setExpenses] = useState([]);
  const [showExpenseModal, setShowExpenseModal] = useState(false);
  const [newExpense, setNewExpense] = useState({
    title: '', amount: 0, category: 'Autre', description: '', date: ''
  });
  
  // Certifications
  const [certifications, setCertifications] = useState([]);
  const [showCertificationModal, setShowCertificationModal] = useState(false);
  const [newCertification, setNewCertification] = useState({
    name: '', type: 'certification', issuer: '', description: '', obtained_date: ''
  });

  // Comptabilité - Débit et Crédit
  const [debits, setDebits] = useState([]);
  const [credits, setCredits] = useState([]);
  // Filtre intervalle de dates pour Compte de Résultat
  const [crDateFrom, setCrDateFrom] = useState('');
  const [crDateTo, setCrDateTo] = useState('');
  // Recherche pour Compte de Résultat
  const [crSearch, setCrSearch] = useState('');
  const [showDebitModal, setShowDebitModal] = useState(false);
  const [showDistributePoolModal, setShowDistributePoolModal] = useState(false);
  const [distributing, setDistributing] = useState(false);
  const [notifying, setNotifying] = useState(false);
  const [distributionPreview, setDistributionPreview] = useState([]);
  const [distributionContext, setDistributionContext] = useState({ benefice: 0, pool20: 0 });
  const [showCreditModal, setShowCreditModal] = useState(false);
  // Édition (correction d'erreur de saisie)
  const [editingDebit, setEditingDebit] = useState(null);
  const [editingCredit, setEditingCredit] = useState(null);
  const [showEditDebitModal, setShowEditDebitModal] = useState(false);
  const [showEditCreditModal, setShowEditCreditModal] = useState(false);
  // Confirmation de suppression
  const [deleteTarget, setDeleteTarget] = useState(null); // { type: 'debit'|'credit', id, description }
  const [newDebit, setNewDebit] = useState({
    date: new Date().toISOString().split('T')[0],
    description: '',
    categorie: 'Loyer et charge',
    statut: 'En attente',
    montant: 0
  });
  const [newCredit, setNewCredit] = useState({
    date: new Date().toISOString().split('T')[0],
    description: '',
    annonceur: 'Orange',
    montant: 0
  });

  // Tableau de charges - remplissage direct
  const [chargesTable, setChargesTable] = useState([
    { id: Date.now().toString(), date: new Date().toISOString().split('T')[0], categorie: 'Loyer et charge', description: '', montant: 0, statut: 'En attente' }
  ]);

  // Sélecteurs de période rapide (Tout / Aujourd'hui / Cette semaine / Ce mois)
  const [chargesPeriod, setChargesPeriod] = useState('all');
  const [produitsPeriod, setProduitsPeriod] = useState('all');

  // Catégories personnalisées (chargées depuis l'API)
  const [customChargeCats, setCustomChargeCats] = useState([]);
  const [customProduitCats, setCustomProduitCats] = useState([]);
  const [hiddenChargeDefaults, setHiddenChargeDefaults] = useState([]);
  const [hiddenProduitDefaults, setHiddenProduitDefaults] = useState([]);
  useEffect(() => {
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/financial/categories`)
      .then((r) => {
        setCustomChargeCats(r.data?.charges || []);
        setCustomProduitCats(r.data?.produits || []);
        setHiddenChargeDefaults(r.data?.hiddenDefaults?.charges || []);
        setHiddenProduitDefaults(r.data?.hiddenDefaults?.produits || []);
      })
      .catch(() => {});
  }, []);

  const onChargeCatsChange = (newCustom, newHidden) => {
    setCustomChargeCats(newCustom);
    if (newHidden !== undefined) setHiddenChargeDefaults(newHidden);
  };
  const onProduitCatsChange = (newCustom, newHidden) => {
    setCustomProduitCats(newCustom);
    if (newHidden !== undefined) setHiddenProduitDefaults(newHidden);
  };

  const addChargeRow = () => {
    setChargesTable([...chargesTable, {
      id: Date.now().toString() + Math.random(),
      date: new Date().toISOString().split('T')[0],
      categorie: 'Loyer et charge',
      description: '',
      montant: 0,
      statut: 'En attente'
    }]);
  };

  const updateChargeRow = (id, field, value) => {
    setChargesTable(chargesTable.map(row => row.id === id ? { ...row, [field]: value } : row));
  };

  const removeChargeRow = (id) => {
    setChargesTable(chargesTable.filter(row => row.id !== id));
  };

  const saveChargesToDebits = async () => {
    const validRows = chargesTable.filter(r => r.description && r.montant > 0);
    if (validRows.length === 0) {
      toast.error('Aucune charge valide à enregistrer');
      return;
    }
    try {
      for (const row of validRows) {
        await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/financial/debit`, {
          date: row.date,
          description: row.description,
          categorie: row.categorie,
          statut: row.statut,
          montant: parseFloat(row.montant) || 0
        });
      }
      await loadFinancialTransactions();
      setChargesTable([{
        id: Date.now().toString(),
        date: new Date().toISOString().split('T')[0],
        categorie: 'Loyer et charge',
        description: '',
        montant: 0,
        statut: 'En attente'
      }]);
      toast.success(`${validRows.length} charge(s) enregistrée(s) et synchronisée(s) avec le compte de résultat associés`);
    } catch (error) {
      console.error('Error saving charges:', error);
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  // Distribution du pool 20% en charges "Dividende investisseur"
  const openDistributePool = async () => {
    try {
      const [invRes, finRes] = await Promise.all([
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/investisseurs`),
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/financial/dashboard`),
      ]);
      const investisseurs = (invRes.data?.items || []).filter(i => (i.statut || 'actif') === 'actif');
      const dataDoc = finRes.data || {};
      const debitsArr = dataDoc.debits || [];
      const creditsArr = dataDoc.credits || [];
      const totalCharges = debitsArr.reduce((s, d) => s + (parseFloat(d.montant) || 0), 0);
      const totalProduits = creditsArr.reduce((s, c) => s + (parseFloat(c.montant) || 0), 0);
      const benefice = totalProduits - totalCharges;

      if (benefice <= 0) {
        toast.error("Aucun bénéfice à distribuer (résultat <= 0)");
        return;
      }
      if (investisseurs.length === 0) {
        toast.error("Aucun investisseur actif enregistré");
        return;
      }

      const pool20 = benefice * 0.20;
      const totalMises = investisseurs.reduce((s, i) => s + (parseFloat(i.mise) || 0), 0);
      if (totalMises <= 0) {
        toast.error("Aucune mise enregistrée pour les investisseurs");
        return;
      }

      const preview = investisseurs.map(inv => {
        const mise = parseFloat(inv.mise) || 0;
        const part = (mise / totalMises) * pool20;
        const fullName = `${inv.prenom || ''} ${inv.nom || ''}`.trim() || inv.email || 'Investisseur';
        return { id: inv.id, email: inv.email || '', fullName, mise, share: mise / totalMises, montant: Math.round(part * 100) / 100 };
      });

      setDistributionPreview(preview);
      setDistributionContext({ benefice, pool20 });
      setShowDistributePoolModal(true);
    } catch (e) {
      console.error(e);
      toast.error("Erreur lors du chargement des données");
    }
  };

  const confirmDistributePool = async () => {
    if (distributionPreview.length === 0) return;
    setDistributing(true);
    try {
      const today = new Date().toISOString().split('T')[0];
      for (const p of distributionPreview) {
        await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/financial/debit`, {
          date: today,
          description: `Dividende investisseur — ${p.fullName} (${(p.share * 100).toFixed(2)}% du pool)`,
          categorie: 'Salaires',
          statut: 'En attente',
          montant: p.montant,
        });
      }
      await loadFinancialTransactions();
      toast.success(`${distributionPreview.length} dividende(s) enregistré(s) en charges`);
      setShowDistributePoolModal(false);
      setDistributionPreview([]);
    } catch (e) {
      console.error(e);
      toast.error("Erreur lors de la distribution");
    } finally {
      setDistributing(false);
    }
  };

  const notifyInvestorsByEmail = async () => {
    if (distributionPreview.length === 0) return;
    setNotifying(true);
    try {
      const monthLabel = new Date().toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
      const res = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/investisseurs/notify-dividends`, {
        items: distributionPreview,
        month: monthLabel,
        benefice: distributionContext.benefice,
        pool20: distributionContext.pool20,
      });
      const { sent_count, failed_count, failed } = res.data || {};
      if (sent_count > 0) {
        toast.success(`${sent_count} email(s) envoyé(s) avec succès`);
      }
      if (failed_count > 0) {
        const firstErr = (failed && failed[0]?.error) || 'Erreur inconnue';
        toast.error(`${failed_count} échec(s) : ${firstErr}`);
      }
    } catch (e) {
      console.error(e);
      const detail = e.response?.data?.detail || "Erreur lors de l'envoi des emails";
      toast.error(detail);
    } finally {
      setNotifying(false);
    }
  };

  // Tableau de produits - saisie directe
  const [produitsTable, setProduitsTable] = useState([
    { id: Date.now().toString() + '_p', date: new Date().toISOString().split('T')[0], categorie: 'Location panneau (mensuel)', annonceur: 'Orange', description: '', montant: 0 }
  ]);

  const addProduitRow = () => {
    setProduitsTable([...produitsTable, {
      id: Date.now().toString() + Math.random() + '_p',
      date: new Date().toISOString().split('T')[0],
      categorie: 'Location panneau (mensuel)',
      annonceur: 'Orange',
      description: '',
      montant: 0
    }]);
  };

  const updateProduitRow = (id, field, value) => {
    setProduitsTable(produitsTable.map(row => row.id === id ? { ...row, [field]: value } : row));
  };

  const removeProduitRow = (id) => {
    setProduitsTable(produitsTable.filter(row => row.id !== id));
  };

  const saveProduitsToCredits = async () => {
    const validRows = produitsTable.filter(r => r.description && r.montant > 0);
    if (validRows.length === 0) {
      toast.error('Aucun produit valide à enregistrer');
      return;
    }
    try {
      for (const row of validRows) {
        await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/financial/credit`, {
          date: row.date,
          description: row.description,
          categorie: row.categorie,
          annonceur: row.annonceur,
          montant: parseFloat(row.montant) || 0
        });
      }
      await loadFinancialTransactions();
      setProduitsTable([{
        id: Date.now().toString() + '_p',
        date: new Date().toISOString().split('T')[0],
        categorie: 'Location panneau (mensuel)',
        annonceur: 'Orange',
        description: '',
        montant: 0
      }]);
      toast.success(`${validRows.length} produit(s) enregistré(s) et synchronisé(s) avec le compte de résultat associés`);
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  useEffect(() => {
    loadAllData();
  }, []);

  const loadAllData = async () => {
    try {
      await Promise.all([
        loadCompanyInfo(),
        loadAnalytics(),
        loadAlerts(),
        loadObjectives(),
        loadEmployees(),
        loadDocuments(),
        loadExpenses(),
        loadCertifications(),
        loadFinancialTransactions()
      ]);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadFinancialTransactions = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/financial/transactions`);
      setDebits(response.data.debits || []);
      setCredits(response.data.credits || []);
    } catch (error) {
      console.error('Error loading financial transactions:', error);
    }
  };

  // ===== Modifier / Supprimer un DÉBIT =====
  const handleEditDebit = (debit) => {
    setEditingDebit({ ...debit });
    setShowEditDebitModal(true);
  };

  const handleSaveEditDebit = async () => {
    if (!editingDebit?.description || !(parseFloat(editingDebit.montant) > 0)) {
      toast.error('Description et montant sont obligatoires');
      return;
    }
    try {
      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/api/financial/debit/${editingDebit.id}`,
        {
          date: editingDebit.date,
          description: editingDebit.description,
          categorie: editingDebit.categorie,
          statut: editingDebit.statut,
          montant: parseFloat(editingDebit.montant) || 0
        }
      );
      await loadFinancialTransactions();
      setShowEditDebitModal(false);
      setEditingDebit(null);
      toast.success('Débit modifié avec succès (Compte de Résultat mis à jour)');
    } catch (error) {
      console.error(error);
      toast.error('Erreur lors de la modification');
    }
  };

  const handleDeleteDebit = (debit) => {
    setDeleteTarget({ type: 'debit', id: debit.id, description: debit.description });
  };

  const handleDeleteCredit = (credit) => {
    setDeleteTarget({ type: 'credit', id: credit.id, description: credit.description });
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    const target = deleteTarget;
    setDeleteTarget(null);
    try {
      // Récupérer l'objet complet AVANT suppression pour permettre le undo
      const fullItem = target.type === 'debit'
        ? debits.find(d => d.id === target.id)
        : credits.find(c => c.id === target.id);

      const url = target.type === 'debit'
        ? `${process.env.REACT_APP_BACKEND_URL}/api/financial/debit/${target.id}`
        : `${process.env.REACT_APP_BACKEND_URL}/api/financial/credit/${target.id}`;
      await axios.delete(url);
      await loadFinancialTransactions();

      // Toast avec bouton "Annuler" pour restaurer
      toast.success(
        `${target.type === 'debit' ? 'Débit' : 'Crédit'} supprimé`,
        {
          description: fullItem?.description || '',
          duration: 10000,
          action: fullItem ? {
            label: 'Annuler',
            onClick: async () => {
              try {
                const restoreUrl = target.type === 'debit'
                  ? `${process.env.REACT_APP_BACKEND_URL}/api/financial/debit`
                  : `${process.env.REACT_APP_BACKEND_URL}/api/financial/credit`;
                const payload = target.type === 'debit'
                  ? {
                      date: fullItem.date,
                      description: fullItem.description,
                      categorie: fullItem.categorie,
                      statut: fullItem.statut,
                      montant: fullItem.montant
                    }
                  : {
                      date: fullItem.date,
                      description: fullItem.description,
                      annonceur: fullItem.annonceur,
                      montant: fullItem.montant
                    };
                await axios.post(restoreUrl, payload);
                await loadFinancialTransactions();
                toast.success('Suppression annulée - élément restauré');
              } catch (e) {
                toast.error('Impossible de restaurer');
              }
            }
          } : undefined
        }
      );
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  // ===== Modifier / Supprimer un CRÉDIT =====
  const handleEditCredit = (credit) => {
    setEditingCredit({ ...credit });
    setShowEditCreditModal(true);
  };

  const handleSaveEditCredit = async () => {
    if (!editingCredit?.description || !(parseFloat(editingCredit.montant) > 0)) {
      toast.error('Description et montant sont obligatoires');
      return;
    }
    try {
      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/api/financial/credit/${editingCredit.id}`,
        {
          date: editingCredit.date,
          description: editingCredit.description,
          annonceur: editingCredit.annonceur,
          montant: parseFloat(editingCredit.montant) || 0
        }
      );
      await loadFinancialTransactions();
      setShowEditCreditModal(false);
      setEditingCredit(null);
      toast.success('Crédit modifié avec succès (Compte de Résultat mis à jour)');
    } catch (error) {
      toast.error('Erreur lors de la modification');
    }
  };

  const handleDeleteCreditOld_REMOVED = null;

  const loadCompanyInfo = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/company`);
      setCompanyInfo(response.data);
    } catch (error) {
      console.error('Error loading company info:', error);
    }
  };

  const loadAnalytics = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/company/analytics`);
      setAnalytics(response.data);
    } catch (error) {
      console.error('Error loading analytics:', error);
    }
  };

  const loadAlerts = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/company/alerts`);
      setAlerts(response.data);
    } catch (error) {
      console.error('Error loading alerts:', error);
    }
  };

  const loadObjectives = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/company/objectives`);
      setObjectives(response.data);
    } catch (error) {
      console.error('Error loading objectives:', error);
    }
  };

  const loadEmployees = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/employes`);
      setEmployees(response.data);
    } catch (error) {
      console.error('Error loading employees:', error);
    }
  };

  const loadDocuments = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/company/documents`);
      setDocuments(response.data);
    } catch (error) {
      console.error('Error loading documents:', error);
    }
  };

  const loadExpenses = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/company/expenses`);
      setExpenses(response.data);
    } catch (error) {
      console.error('Error loading expenses:', error);
    }
  };

  const loadCertifications = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/company/certifications`);
      setCertifications(response.data);
    } catch (error) {
      console.error('Error loading certifications:', error);
    }
  };

  // Create Objective
  const handleCreateObjective = async () => {
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/company/objectives`, newObjective);
      toast.success('Objectif créé avec succès');
      setShowObjectiveModal(false);
      setNewObjective({ title: '', description: '', target_value: 0, current_value: 0, unit: 'FCFA', period: 'mensuel', deadline: '' });
      loadObjectives();
    } catch (error) {
      console.error('Error creating objective:', error);
      toast.error('Erreur lors de la création');
    }
  };

  // Update Objective Progress
  const handleUpdateObjective = async (objectiveId, currentValue) => {
    try {
      await axios.patch(`${process.env.REACT_APP_BACKEND_URL}/api/company/objectives/${objectiveId}`, {
        current_value: currentValue
      });
      toast.success('Objectif mis à jour');
      loadObjectives();
    } catch (error) {
      console.error('Error updating objective:', error);
      toast.error('Erreur lors de la mise à jour');
    }
  };

  // Create Document
  const handleCreateDocument = async () => {
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/company/documents`, newDocument);
      toast.success('Document enregistré');
      setShowDocumentModal(false);
      setNewDocument({ name: '', category: 'Administratif', file_url: '', description: '', expiry_date: '' });
      loadDocuments();
      loadAlerts(); // Refresh alerts
    } catch (error) {
      console.error('Error creating document:', error);
      toast.error('Erreur lors de l\'enregistrement');
    }
  };

  // Create Expense
  const handleCreateExpense = async () => {
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/company/expenses`, newExpense);
      toast.success('Dépense enregistrée');
      setShowExpenseModal(false);
      setNewExpense({ title: '', amount: 0, category: 'Autre', description: '', date: '' });
      loadExpenses();
      loadAnalytics(); // Refresh analytics
    } catch (error) {
      console.error('Error creating expense:', error);
      toast.error('Erreur lors de l\'enregistrement');
    }
  };

  // Create Certification
  const handleCreateCertification = async () => {
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/company/certifications`, newCertification);
      toast.success('Certification ajoutée');
      setShowCertificationModal(false);
      setNewCertification({ name: '', type: 'certification', issuer: '', description: '', obtained_date: '' });
      loadCertifications();
    } catch (error) {
      console.error('Error creating certification:', error);
      toast.error('Erreur lors de l\'ajout');
    }
  };

  // Add Employee
  const handleAddEmployee = async () => {
    try {
      if (!newEmployee.nom || !newEmployee.prenom || !newEmployee.poste) {
        toast.error('Veuillez remplir tous les champs obligatoires');
        return;
      }
      
      // Combine prenom and nom for backend
      const employeeData = {
        nom: `${newEmployee.prenom} ${newEmployee.nom}`,
        poste: newEmployee.poste,
        departement: newEmployee.departement || 'Général',
        email: newEmployee.email,
        telephone: newEmployee.telephone
      };
      
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/employes`, employeeData);
      toast.success('Membre ajouté avec succès');
      setShowEmployeeModal(false);
      setNewEmployee({ nom: '', prenom: '', poste: '', departement: 'Général', email: '', telephone: '' });
      loadEmployees();
    } catch (error) {
      console.error('Error adding employee:', error);
      toast.error('Erreur lors de l\'ajout du membre');
    }
  };

  // Delete Employee
  const handleDeleteEmployee = async (memberOrId) => {
    const employeeId = typeof memberOrId === 'string' ? memberOrId : memberOrId?.id;
    const label = typeof memberOrId === 'object' ? (memberOrId?.nom || memberOrId?.name || 'ce membre') : 'ce membre';
    if (!employeeId) return;
    try {
      if (!window.confirm(`Êtes-vous sûr de vouloir supprimer ${label} ?`)) {
        return;
      }

      await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/api/employes/${employeeId}`);
      toast.success('Membre supprimé avec succès');
      loadEmployees();
    } catch (error) {
      console.error('Error deleting employee:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  // Edit Employee : ouvre la modale pré-remplie
  const openEditEmployee = (m) => {
    // Split "Prenom NOM" éventuel
    const fullName = m.nom || m.name || '';
    const parts = fullName.split(' ');
    let prenom = m.prenom || '';
    let nom = '';
    if (m.prenom) {
      nom = fullName.replace(m.prenom, '').trim();
    } else if (parts.length >= 2) {
      prenom = parts[0];
      nom = parts.slice(1).join(' ');
    } else {
      nom = fullName;
    }
    setEditingEmployeeId(m.id);
    setNewEmployee({
      nom: nom,
      prenom: prenom,
      poste: m.poste || m.role || '',
      departement: m.departement || m.department || 'Général',
      email: m.email || '',
      telephone: m.telephone || '',
    });
    setShowEmployeeModal(true);
  };

  // Update Employee
  const handleUpdateEmployee = async () => {
    try {
      if (!editingEmployeeId) return;
      if (!newEmployee.nom || !newEmployee.prenom || !newEmployee.poste) {
        toast.error('Veuillez remplir tous les champs obligatoires');
        return;
      }
      const employeeData = {
        nom: `${newEmployee.prenom} ${newEmployee.nom}`.trim(),
        prenom: newEmployee.prenom,
        poste: newEmployee.poste,
        departement: newEmployee.departement || 'Général',
        email: newEmployee.email,
        telephone: newEmployee.telephone,
      };
      await axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/employes/${editingEmployeeId}`, employeeData);
      toast.success('Membre modifié avec succès');
      setShowEmployeeModal(false);
      setEditingEmployeeId(null);
      setNewEmployee({ nom: '', prenom: '', poste: '', departement: 'Général', email: '', telephone: '' });
      loadEmployees();
    } catch (error) {
      console.error('Error updating employee:', error);
      toast.error('Erreur lors de la modification');
    }
  };

  // Export Functions
  const handleExportExpenses = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/company/export/expenses`);
      const dataStr = JSON.stringify(response.data, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `depenses_${new Date().toISOString().split('T')[0]}.json`;
      link.click();
      toast.success('Export réussi');
    } catch (error) {
      console.error('Error exporting:', error);
      toast.error('Erreur lors de l\'export');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR').format(amount) + ' FCFA';
  };

  const getProgressColor = (progress) => {
    if (progress >= 100) return 'bg-green-500';
    if (progress >= 75) return 'bg-blue-500';
    if (progress >= 50) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  const getAlertColor = (priority) => {
    if (priority === 'high') return 'border-red-500 bg-red-50';
    if (priority === 'medium') return 'border-yellow-500 bg-yellow-50';
    return 'border-blue-500 bg-blue-50';
  };

  if (loading) {
    return (
      <Layout user={user} onLogout={onLogout} currentPage="entreprise">
        <div className="flex items-center justify-center h-screen">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} onLogout={onLogout} currentPage="entreprise">
      <div className="space-y-4 sm:space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 sm:gap-4 min-w-0">
            <div className="w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
              <Building2 className="h-6 w-6 sm:h-8 sm:w-8 text-white" />
            </div>
            <div className="min-w-0">
              <h1 className="text-xl sm:text-3xl font-bold truncate">{companyInfo?.nom || 'SESAM'}</h1>
              <p className="text-xs sm:text-base text-gray-600 truncate hidden sm:block">{companyInfo?.slogan || 'Service aux Sociétés Africaines en Marketing'}</p>
            </div>
          </div>
        </div>

        {/* Tabs Navigation */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <div className="overflow-x-auto -mx-3 sm:mx-0 px-3 sm:px-0">
            <TabsList className="inline-flex w-max sm:grid sm:w-full sm:grid-cols-10 mb-4 sm:mb-6 gap-1 sm:gap-0">
              <TabsTrigger value="overview" className="gap-1 sm:gap-2 text-xs sm:text-sm whitespace-nowrap">
                <TrendingUp className="h-3 w-3 sm:h-4 sm:w-4" />
                <span className="hidden sm:inline">Vue d'ensemble</span>
                <span className="sm:hidden">Vue</span>
              </TabsTrigger>
              <TabsTrigger value="info" className="gap-1 sm:gap-2 text-xs sm:text-sm whitespace-nowrap">
                <Building2 className="h-3 w-3 sm:h-4 sm:w-4" />
                <span className="hidden sm:inline">Informations</span>
                <span className="sm:hidden">Info</span>
              </TabsTrigger>
              <TabsTrigger value="objectives" className="gap-1 sm:gap-2 text-xs sm:text-sm whitespace-nowrap">
                <Target className="h-3 w-3 sm:h-4 sm:w-4" />
                Objectifs
              </TabsTrigger>
              <TabsTrigger value="team" className="gap-1 sm:gap-2 text-xs sm:text-sm whitespace-nowrap">
                <Users className="h-3 w-3 sm:h-4 sm:w-4" />
                Équipe
              </TabsTrigger>
              <TabsTrigger value="documents" className="gap-1 sm:gap-2 text-xs sm:text-sm whitespace-nowrap">
                <FileText className="h-3 w-3 sm:h-4 sm:w-4" />
                Docs
              </TabsTrigger>
              <TabsTrigger value="finances" className="gap-1 sm:gap-2 text-xs sm:text-sm whitespace-nowrap">
                <DollarSign className="h-3 w-3 sm:h-4 sm:w-4" />
                <span className="hidden sm:inline">Comptabilité</span>
                <span className="sm:hidden">Compta</span>
              </TabsTrigger>
              <TabsTrigger value="investisseurs" className="gap-1 sm:gap-2 text-xs sm:text-sm whitespace-nowrap">
                <Users className="h-3 w-3 sm:h-4 sm:w-4" />
                <span className="hidden sm:inline">Investisseurs</span>
                <span className="sm:hidden">Invest.</span>
              </TabsTrigger>
              <TabsTrigger value="certifications" className="gap-1 sm:gap-2 text-xs sm:text-sm whitespace-nowrap">
                <Award className="h-3 w-3 sm:h-4 sm:w-4" />
                <span className="hidden sm:inline">Certifications</span>
                <span className="sm:hidden">Certif.</span>
              </TabsTrigger>
              <TabsTrigger value="revenus" className="gap-1 sm:gap-2 text-xs sm:text-sm whitespace-nowrap">
                <DollarSign className="h-3 w-3 sm:h-4 sm:w-4" />
                Revenus
              </TabsTrigger>
              <TabsTrigger value="reports" className="gap-1 sm:gap-2 text-xs sm:text-sm whitespace-nowrap">
                <BarChart3 className="h-3 w-3 sm:h-4 sm:w-4" />
                Rapports
              </TabsTrigger>
            </TabsList>
          </div>

          {/* VUE D'ENSEMBLE */}
          <TabsContent value="overview" className="space-y-6">
            {/* Alertes */}
            {alerts.length > 0 && (
              <Card className="border-2 border-orange-200">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Bell className="h-6 w-6 text-orange-600" />
                    Alertes ({alerts.length})
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {alerts.slice(0, 5).map((alert) => (
                    <div key={alert.id} className={`p-4 rounded-lg border-l-4 ${getAlertColor(alert.priority)}`}>
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="font-semibold">{alert.title}</p>
                          <p className="text-sm text-gray-600">{alert.message}</p>
                        </div>
                        <Badge className={alert.priority === 'high' ? 'bg-red-500' : 'bg-yellow-500'}>
                          {alert.priority === 'high' ? 'Urgent' : 'Important'}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}

            <EntrepriseOverview analytics={analytics} />
          </TabsContent>

          {/* INFORMATIONS GÉNÉRALES */}
          <TabsContent value="info" className="space-y-6" data-testid="entreprise-info-tab">
            {/* Header avec bouton Modifier */}
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-bold text-[#1C2A46]">Informations de l'entreprise</h2>
                <p className="text-sm text-slate-500">Données légales, contacts et informations commerciales</p>
              </div>
              {user?.role === 'admin' && (
                <Button
                  onClick={() => setShowEditCompany(true)}
                  className="bg-blue-600 hover:bg-blue-700 text-white gap-2"
                  data-testid="edit-company-info-btn"
                >
                  <Edit2 className="h-4 w-4" /> Modifier
                </Button>
              )}
            </div>
            {(() => {
              const c = companyInfo || {};
              const Section = ({ icon: Icon, title, badge, children }) => (
                <Card className="border border-slate-100 shadow-sm">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between mb-5">
                      <h3 className="flex items-center gap-2 text-base font-bold text-[#1C2A46]">
                        <Icon className="h-5 w-5 text-blue-600" />
                        {title}
                      </h3>
                      {badge ? <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">{badge}</Badge> : null}
                    </div>
                    {children}
                  </CardContent>
                </Card>
              );
              const Field = ({ label, value, link }) => (
                <div className="grid grid-cols-[140px_1fr] gap-3 items-baseline py-2.5 border-b border-slate-50 last:border-0">
                  <p className="text-sm text-slate-500">{label}</p>
                  {link ? (
                    <a href={link} className="text-sm font-medium text-blue-600 hover:underline break-all" target="_blank" rel="noreferrer">{value || '—'}</a>
                  ) : (
                    <p className="text-sm font-medium text-[#1C2A46] break-words">{value || '—'}</p>
                  )}
                </div>
              );
              const InfoLine = ({ icon: Icon, label, value }) => (
                <div className="flex items-center justify-between py-2.5 border-b border-slate-50 last:border-0">
                  <span className="text-sm text-slate-500 flex items-center gap-2"><Icon className="h-4 w-4 text-blue-500" />{label}</span>
                  <span className="text-sm font-medium text-[#1C2A46]">{value || '—'}</span>
                </div>
              );
              return (
                <div className="space-y-6">
                  {/* Ligne 1: 3 colonnes */}
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
                    {/* Informations générales */}
                    <Section icon={Info} title="Informations générales">
                      <div className="divide-y divide-slate-50">
                        <Field label="Raison sociale" value={c.nom || 'SESAM'} />
                        <Field label="Forme juridique" value={c.forme_juridique || c.description || "Service aux Sociétés Africaines en Marketing"} />
                        <Field label="Numéro d'identification" value={c.rccm || 'RC/BG/2020/B/12345'} />
                        <Field label="Numéro contribuable" value={c.nif || 'M1234567890123'} />
                        <Field label="Date de création" value={c.date_creation || '15 mars 2005'} />
                        <Field label="Pays" value={(<span>🇨🇫 {c.pays || 'République Centrafricaine'}</span>)} />
                        <Field label="Ville" value={c.ville || 'Bangui'} />
                        <Field label="Adresse" value={c.adresse || "Avenue de l'Indépendance, Bangui, Centre ville, République Centrafricaine"} />
                        <Field label="Site web" value={c.site_web || 'www.sesamrca.com'} link={c.site_web ? (c.site_web.startsWith('http') ? c.site_web : 'https://' + c.site_web) : 'https://www.sesamrca.com'} />
                      </div>
                    </Section>

                    {/* Informations de contact */}
                    <Section icon={Users} title="Informations de contact" badge="Principal">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center">
                          <UsersIcon className="h-6 w-6 text-slate-400" />
                        </div>
                        <div>
                          <p className="font-bold text-[#1C2A46]">{c.contact_principal || 'M. Jean Dupont'}</p>
                          <p className="text-xs text-slate-500">{c.contact_fonction || 'Responsable Communication'}</p>
                        </div>
                      </div>
                      <div className="space-y-0">
                        <InfoLine icon={Phone} label="Téléphone" value={c.telephone || '+236 21 61 25 60'} />
                        <InfoLine icon={Phone} label="Téléphone fixe" value={c.telephone_fixe || '+236 22 00 00 00'} />
                        <InfoLine icon={Mail} label="Email" value={c.email || 'sesambangui@gmail.com'} />
                        <InfoLine icon={Mail} label="Autre email" value={c.email_secondaire || 'info@sesamrca.com'} />
                      </div>
                      <Button variant="outline" className="w-full mt-4 gap-2 bg-blue-50 hover:bg-blue-100 border-blue-200 text-blue-700">
                        Voir tous les contacts (3)
                      </Button>
                    </Section>

                    {/* Informations commerciales */}
                    <Section icon={TrendingUp} title="Informations commerciales">
                      <div className="divide-y divide-slate-50">
                        <Field label="Type de client" value={c.type_client || 'Entreprise'} />
                        <div className="grid grid-cols-[140px_1fr] gap-3 items-baseline py-2.5 border-b border-slate-50">
                          <p className="text-sm text-slate-500">Statut</p>
                          <span className="inline-flex items-center gap-1.5 text-sm font-medium text-emerald-700">
                            <span className="w-2 h-2 bg-emerald-500 rounded-full"></span>
                            {c.statut_commercial || 'Actif'}
                          </span>
                        </div>
                        <div className="grid grid-cols-[140px_1fr] gap-3 items-baseline py-2.5 border-b border-slate-50">
                          <p className="text-sm text-slate-500">Responsable commercial</p>
                          <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-blue-50 text-blue-700 text-sm font-medium border border-blue-100">
                            <UsersIcon className="h-3.5 w-3.5" />
                            {c.responsable_commercial || 'Admin SESAM'}
                          </span>
                        </div>
                        <Field label="Source" value={c.source || 'Prospection'} />
                        <Field label="Devise" value={c.devise || 'FCFA'} />
                        <Field label="Conditions de paiement" value={c.conditions_paiement || '30 jours'} />
                        <Field label="Plafond de crédit" value={c.plafond_credit || '15 000 000 FCFA'} />
                        <Field label="Remise par défaut" value={c.remise || '0%'} />
                      </div>
                    </Section>
                  </div>

                  {/* Ligne 2 : Adresses + Documents */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                    <Section icon={MapPin} title="Adresses">
                      <div className="space-y-4">
                        <div>
                          <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100 mb-2">Adresse principale</Badge>
                          <p className="text-sm text-[#1C2A46] leading-relaxed">{c.adresse || "Avenue de l'Indépendance, Bangui,"}<br />Centre ville, République Centrafricaine</p>
                        </div>
                        <div>
                          <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100 mb-2">Adresse de facturation</Badge>
                          <p className="text-sm text-[#1C2A46] leading-relaxed">SESAM<br />BP 01 Bangui,<br />République Centrafricaine</p>
                        </div>
                        <Button variant="outline" className="w-full gap-2 bg-blue-50 hover:bg-blue-100 border-blue-200 text-blue-700">
                          Voir toutes les adresses (2) →
                        </Button>
                      </div>
                    </Section>

                    <Section icon={FileText} title="Documents liés">
                      <ul className="space-y-3">
                        {[
                          { name: 'Registre de commerce.pdf', size: '1.2 Mo', date: '12/05/2023' },
                          { name: 'Attestation fiscale.pdf', size: '0.8 Mo', date: '12/05/2023' },
                          { name: 'Contrat de partenariat.pdf', size: '2.4 Mo', date: '12/05/2023' },
                        ].map((doc) => (
                          <li key={doc.name} className="flex items-center gap-3 p-3 rounded-lg hover:bg-slate-50">
                            <div className="w-10 h-10 rounded-lg bg-red-50 flex items-center justify-center flex-shrink-0">
                              <FileText className="h-5 w-5 text-red-500" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-semibold text-[#1C2A46] truncate">{doc.name}</p>
                              <p className="text-xs text-slate-500">PDF · {doc.size} · {doc.date}</p>
                            </div>
                            <button className="w-9 h-9 rounded-full hover:bg-slate-100 flex items-center justify-center text-slate-500" title="Télécharger">
                              <Download className="h-4 w-4" />
                            </button>
                          </li>
                        ))}
                      </ul>
                      <Button variant="outline" className="w-full mt-4 gap-2 bg-blue-50 hover:bg-blue-100 border-blue-200 text-blue-700">
                        Voir tous les documents (5) →
                      </Button>
                    </Section>
                  </div>
                </div>
              );
            })()}
          </TabsContent>

          {/* OBJECTIFS */}
          <TabsContent value="objectives" className="space-y-4">
            <ObjectifsTab currentUser={user} />
          </TabsContent>

          {/* ÉQUIPE - Interface améliorée et compacte */}
{/* TEAM - Organigramme intégré */}
{/* TEAM - Organigramme hiérarchique (style maquette client) */}
          <TabsContent value="team" className="space-y-6" data-testid="entreprise-team-tab">
            {(() => {
              // Initiales 2 lettres
              const initials = (s = '') =>
                s.split(' ').filter(Boolean).slice(0, 2).map((p) => p[0]?.toUpperCase()).join('') || '?';

              // Détection rôle directeur (CEO en haut)
              const ceo = employees.find((m) => {
                const p = (m.poste || m.role || '').toLowerCase();
                return p.includes('directeur') && !p.includes('responsable');
              }) || employees[0] || null;

              // Répartition par département
              const direction = [];
              const commercial = [];
              const technique = [];
              employees.forEach((m) => {
                if (ceo && m.id === ceo.id) return; // exclure CEO du listing
                const dep = (m.departement || m.department || '').toLowerCase();
                const poste = (m.poste || m.role || '').toLowerCase();
                if (dep.includes('direction') || poste.includes('directeur') || poste.includes('admin') || poste.includes('associé') || poste.includes('coordon')) direction.push(m);
                else if (dep.includes('commercial') || poste.includes('commercial') || poste.includes('vente') || poste.includes('marketing')) commercial.push(m);
                else technique.push(m);
              });

              // Couleurs avatar par lettre/index
              const avatarColors = [
                'bg-purple-500', 'bg-blue-500', 'bg-pink-500',
                'bg-green-500', 'bg-orange-500', 'bg-teal-500',
                'bg-indigo-500', 'bg-rose-500',
              ];
              const colorFor = (s = '') =>
                avatarColors[(s.charCodeAt(0) || 0) % avatarColors.length];

              const roleBadgeStyle = (poste = '') => {
                const p = poste.toLowerCase();
                if (p.includes('directeur')) return 'bg-purple-100 text-purple-700';
                if (p.includes('associé')) return 'bg-violet-100 text-violet-700';
                if (p.includes('responsable')) return 'bg-green-100 text-green-700';
                if (p.includes('commercial')) return 'bg-emerald-100 text-emerald-700';
                if (p.includes('technicien')) return 'bg-amber-100 text-amber-700';
                if (p.includes('coordon')) return 'bg-blue-100 text-blue-700';
                return 'bg-slate-100 text-slate-700';
              };

              // Carte membre détaillée
              const MemberCard = ({ m }) => (
                <div
                  className="bg-white border border-slate-200 rounded-xl shadow-sm hover:shadow-md transition w-[200px] flex-shrink-0 group"
                  data-testid={`team-member-${m.id || m.email}`}
                >
                  <div className="flex flex-col items-center p-4 pb-3 relative">
                    <div className={`w-14 h-14 rounded-full ${colorFor(m.nom || m.name || m.email)} flex items-center justify-center text-white text-base font-bold shadow-sm`}>
                      {initials(m.nom || m.name || m.email)}
                    </div>
                    <p className="text-sm font-bold text-[#1C2A46] mt-2.5 text-center">
                      {m.nom || m.name || m.email}
                    </p>
                    <span className={`mt-1.5 text-[10px] font-semibold px-2.5 py-0.5 rounded-full ${roleBadgeStyle(m.poste || m.role)}`}>
                      {m.poste || m.role || '—'}
                    </span>
                    {m.email && (
                      <p className="text-[10px] text-slate-500 mt-2 truncate w-full text-center">{m.email}</p>
                    )}
                    {m.telephone && (
                      <p className="text-[10px] text-slate-500">{m.telephone}</p>
                    )}
                    {/* Actions admin : modifier / supprimer */}
                    {user?.role === 'admin' && (
                      <div className="flex gap-1.5 mt-3 w-full opacity-0 group-hover:opacity-100 transition-opacity">
                        <Button
                          size="sm"
                          variant="outline"
                          className="flex-1 h-7 text-[11px] gap-1"
                          onClick={() => openEditEmployee(m)}
                          data-testid={`edit-member-${m.id || m.email}`}
                        >
                          <Edit2 className="h-3 w-3" />
                          Modifier
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 px-2 text-[11px] text-rose-600 border-rose-200 hover:bg-rose-50"
                          onClick={() => handleDeleteEmployee(m)}
                          data-testid={`delete-member-${m.id || m.email}`}
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              );

              const departments = [
                { key: 'direction', title: 'Direction', members: direction, bg: 'bg-blue-50', border: 'border-blue-200', icon: <Briefcase className="h-4 w-4 text-blue-600" /> },
                { key: 'commercial', title: 'Commercial', members: commercial, bg: 'bg-emerald-50', border: 'border-emerald-200', icon: <TrendingUp className="h-4 w-4 text-emerald-600" /> },
                { key: 'technique', title: 'Technique', members: technique, bg: 'bg-amber-50', border: 'border-amber-200', icon: <Activity className="h-4 w-4 text-amber-600" /> },
              ];

              return (
                <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-5">
                  {/* SIDEBAR GAUCHE */}
                  <div className="space-y-3">
                    <Button
                      onClick={() => setShowEmployeeModal(true)}
                      className="w-full justify-center gap-2 bg-blue-600 hover:bg-blue-700 h-11"
                      data-testid="add-member-btn"
                    >
                      <Plus className="h-4 w-4" />
                      Ajouter un membre
                    </Button>
                    <div className="bg-white border border-slate-200 rounded-xl p-1 space-y-0.5">
                      {[
                        { label: "Vue d'ensemble", icon: PieChart, active: true, testid: 'view-overview' },
                        { label: 'Par département', icon: Briefcase, testid: 'view-by-dept' },
                        { label: 'Imprimer', icon: FileText, testid: 'print-team', onClick: () => window.print() },
                        { label: 'Exporter (PDF)', icon: Download, testid: 'export-team-pdf' },
                      ].map((b) => (
                        <button
                          key={b.label}
                          onClick={b.onClick}
                          className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition ${
                            b.active ? 'bg-blue-50 text-blue-700 font-semibold' : 'text-slate-700 hover:bg-slate-50'
                          }`}
                          data-testid={b.testid}
                        >
                          <b.icon className="h-4 w-4 flex-shrink-0" />
                          {b.label}
                        </button>
                      ))}
                    </div>
                    <Card className="border border-slate-100 shadow-sm">
                      <CardContent className="p-4">
                        <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-3">Statistiques</h3>
                        <div className="space-y-2.5 text-sm">
                          <div className="flex items-center justify-between">
                            <span className="text-slate-600 flex items-center gap-2"><Users className="h-3.5 w-3.5 text-slate-400" />Membres</span>
                            <span className="font-bold text-[#1C2A46]">{employees.length}</span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-slate-600 flex items-center gap-2"><Briefcase className="h-3.5 w-3.5 text-slate-400" />Départements</span>
                            <span className="font-bold text-[#1C2A46]">{departments.filter(d => d.members.length > 0 || (d.key === 'direction')).length}</span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-slate-600 flex items-center gap-2"><Briefcase className="h-3.5 w-3.5 text-slate-400" />Postes</span>
                            <span className="font-bold text-[#1C2A46]">{new Set(employees.map(e => e.poste || e.role)).size}</span>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  {/* CANVAS organigramme */}
                  <Card className="border border-slate-100 shadow-sm">
                    <CardContent className="p-6">
                      {/* Header */}
                      <div className="flex items-center justify-between flex-wrap gap-3 mb-8">
                        <h2 className="text-lg font-bold text-[#1C2A46]">Organigramme de l'équipe SESAM</h2>
                        <div className="flex items-center gap-2">
                          <div className="inline-flex items-center bg-white border border-slate-200 rounded-lg overflow-hidden">
                            <button className="w-9 h-9 hover:bg-slate-50 text-slate-600 font-bold text-lg">−</button>
                            <span className="px-3 text-sm font-semibold text-slate-700 border-x border-slate-200 h-9 flex items-center">100%</span>
                            <button className="w-9 h-9 hover:bg-slate-50 text-slate-600 font-bold text-lg">+</button>
                          </div>
                          <Button variant="outline" className="gap-2 h-9">
                            <X className="h-3.5 w-3.5" />
                            Adapter à l'écran
                          </Button>
                        </div>
                      </div>

                      {/* CEO root */}
                      <div className="flex justify-center mb-6">
                        {ceo ? (
                          <div className="bg-white border border-slate-200 rounded-xl shadow-sm px-6 py-4 flex items-center gap-4 min-w-[280px]">
                            <div className={`w-12 h-12 rounded-full ${colorFor(ceo.nom || ceo.name || ceo.email)} flex items-center justify-center text-white text-sm font-bold`}>
                              {initials(ceo.nom || ceo.name || ceo.email)}
                            </div>
                            <div>
                              <p className="text-sm font-bold text-[#1C2A46]">{ceo.nom || ceo.name || ceo.email}</p>
                              <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full inline-block mt-0.5 ${roleBadgeStyle(ceo.poste || ceo.role)}`}>
                                {ceo.poste || ceo.role || 'Directeur'}
                              </span>
                              <p className="text-[11px] text-slate-500 mt-1">Direction Générale</p>
                            </div>
                          </div>
                        ) : null}
                      </div>

                      {/* Trait vertical CEO → départements */}
                      <div className="flex justify-center"><div className="w-px h-8 bg-slate-300"></div></div>
                      {/* Ligne horizontale */}
                      <div className="grid grid-cols-3 max-w-5xl mx-auto relative">
                        <div className="absolute left-[16%] right-[16%] top-0 h-px bg-slate-300"></div>
                        {departments.map((_, i) => (
                          <div key={i} className="flex justify-center"><div className="w-px h-8 bg-slate-300"></div></div>
                        ))}
                      </div>

                      {/* 3 départements */}
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto mb-6">
                        {departments.map((dept) => (
                          <div key={dept.key} className={`${dept.bg} ${dept.border} border rounded-xl px-4 py-3.5 text-center shadow-sm`}>
                            <p className="text-sm font-bold text-[#1C2A46] flex items-center justify-center gap-2">
                              {dept.icon}
                              {dept.title}
                            </p>
                            <p className="text-xs text-slate-500 mt-0.5">{dept.members.length} membre{dept.members.length > 1 ? 's' : ''}</p>
                          </div>
                        ))}
                      </div>

                      {/* Trait Dept → membres */}
                      <div className="grid grid-cols-3 max-w-5xl mx-auto">
                        {departments.map((d, i) => (
                          <div key={i} className="flex justify-center"><div className="w-px h-6 bg-slate-300"></div></div>
                        ))}
                      </div>

                      {/* Membres par département */}
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
                        {departments.map((dept) => (
                          <div key={dept.key} className="flex flex-wrap justify-center gap-3" data-testid={`dept-${dept.key}-members`}>
                            {dept.members.length === 0 ? (
                              <div className="bg-white border border-dashed border-slate-200 rounded-xl p-6 text-center text-xs text-slate-400 w-[200px]">
                                Aucun membre
                              </div>
                            ) : (
                              dept.members.map((m) => <MemberCard key={m.id || m.email} m={m} />)
                            )}
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </div>
              );
            })()}
          </TabsContent>
          <TabsContent value="documents" className="space-y-6">
            {/* Documents (éditeur + upload) */}
            <DocumentsSection />
          </TabsContent>

          {/* COMPTABILITÉ */}
          <TabsContent value="finances" className="space-y-6">
            {comptaView === 'menu' && (
              <div className="space-y-6" data-testid="compta-menu-view">
                <h2 className="text-2xl font-bold text-gray-900">Comptabilité</h2>
                <p className="text-sm text-gray-600">Choisissez un module comptable à consulter :</p>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  <Card
                    onClick={() => setComptaView('cr')}
                    className="cursor-pointer hover:shadow-xl transition-all border-2 border-indigo-100 hover:border-indigo-500 group"
                    data-testid="compta-card-cr"
                  >
                    <CardContent className="p-8 flex flex-col items-start gap-4">
                      <div className="p-4 bg-indigo-100 rounded-xl group-hover:bg-indigo-600 group-hover:text-white transition-colors">
                        <FileBarChart className="h-8 w-8 text-indigo-600 group-hover:text-white" />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-gray-900">Compte de Résultat</h3>
                        <p className="text-sm text-gray-600 mt-1">
                          Charges, produits, résultat net et classification PCG. Saisie directe des débits/crédits.
                        </p>
                      </div>
                      <div className="flex items-center gap-1 text-indigo-600 font-semibold text-sm mt-2 group-hover:gap-2 transition-all">
                        Ouvrir <ChevronRight className="h-4 w-4" />
                      </div>
                    </CardContent>
                  </Card>

                  <Card
                    onClick={() => setComptaView('bilan')}
                    className="cursor-pointer hover:shadow-xl transition-all border-2 border-amber-100 hover:border-amber-500 group"
                    data-testid="compta-card-bilan"
                  >
                    <CardContent className="p-8 flex flex-col items-start gap-4">
                      <div className="p-4 bg-amber-100 rounded-xl group-hover:bg-amber-600 group-hover:text-white transition-colors">
                        <Scale className="h-8 w-8 text-amber-600 group-hover:text-white" />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-gray-900">Bilan</h3>
                        <p className="text-sm text-gray-600 mt-1">
                          Actif et Passif. Immobilisations, stocks, créances, dettes et capitaux propres.
                        </p>
                      </div>
                      <div className="flex items-center gap-1 text-amber-600 font-semibold text-sm mt-2 group-hover:gap-2 transition-all">
                        Ouvrir <ChevronRight className="h-4 w-4" />
                      </div>
                    </CardContent>
                  </Card>

                  <Card
                    onClick={() => navigate('/tableau-tresorerie')}
                    className="cursor-pointer hover:shadow-xl transition-all border-2 border-blue-100 hover:border-blue-500 group"
                    data-testid="compta-card-tresorerie"
                  >
                    <CardContent className="p-8 flex flex-col items-start gap-4">
                      <div className="p-4 bg-blue-100 rounded-xl group-hover:bg-blue-600 group-hover:text-white transition-colors">
                        <Wallet className="h-8 w-8 text-blue-600 group-hover:text-white" />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-gray-900">Tableau de Trésorerie</h3>
                        <p className="text-sm text-gray-600 mt-1">
                          Encaissements & décaissements par catégorie. Évolution mensuelle du solde de trésorerie.
                        </p>
                      </div>
                      <div className="flex items-center gap-1 text-blue-600 font-semibold text-sm mt-2 group-hover:gap-2 transition-all">
                        Ouvrir <ChevronRight className="h-4 w-4" />
                      </div>
                    </CardContent>
                  </Card>

                  <Card
                    onClick={() => navigate('/tableau-comptable?tab=factures')}
                    className="cursor-pointer hover:shadow-xl transition-all border-2 border-emerald-100 hover:border-emerald-500 group"
                    data-testid="compta-card-factures"
                  >
                    <CardContent className="p-8 flex flex-col items-start gap-4">
                      <div className="p-4 bg-emerald-100 rounded-xl group-hover:bg-emerald-600 group-hover:text-white transition-colors">
                        <Receipt className="h-8 w-8 text-emerald-600 group-hover:text-white" />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-gray-900">Factures</h3>
                        <p className="text-sm text-gray-600 mt-1">
                          Liste de toutes les factures par date. Auto-comptabilité (Trésorerie / Bilan / Résultat) selon la catégorie.
                        </p>
                      </div>
                      <div className="flex items-center gap-1 text-emerald-600 font-semibold text-sm mt-2 group-hover:gap-2 transition-all">
                        Ouvrir <ChevronRight className="h-4 w-4" />
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>
            )}

            {comptaView === 'bilan' && (
              <BilanPanel onBack={() => setComptaView('menu')} />
            )}

            {comptaView === 'compte_bancaire' && (
              <CompteBancairePanel onBack={() => setComptaView('menu')} />
            )}

            {comptaView === 'cr' && (
              <>
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="flex items-center gap-3">
                    <Button variant="outline" size="sm" onClick={() => setComptaView('menu')} className="gap-1" data-testid="cr-back-btn">
                      <ArrowLeft className="h-4 w-4" /> Retour
                    </Button>
                    <DownloadFinancePdfButton
                      type="compte-resultat"
                      testId="cr-admin-download-pdf"
                    />
                    <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                      <FileBarChart className="h-6 w-6 text-indigo-600" /> Compte de Résultat
                    </h2>
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    <Button onClick={() => setShowDebitModal(true)} className="bg-red-600 hover:bg-red-700 gap-2" data-testid="add-debit-btn">
                      <Plus className="h-4 w-4" /> Débit
                    </Button>
                    <Button onClick={() => setShowCreditModal(true)} className="bg-green-600 hover:bg-green-700 gap-2" data-testid="add-credit-btn">
                      <Plus className="h-4 w-4" /> Crédit
                    </Button>
                    <Button onClick={openDistributePool} className="bg-purple-600 hover:bg-purple-700 gap-2" data-testid="distribute-pool-btn" title="Distribuer 20% du bénéfice en dividendes investisseurs">
                      <Users className="h-4 w-4" /> Distribuer pool 20%
                    </Button>
                  </div>
                </div>

                {/* Filtre par intervalle de dates + recherche */}
                <div className="flex items-center justify-end gap-2 flex-wrap">
                  <SearchBox value={crSearch} onChange={setCrSearch} color="indigo" testIdPrefix="cr-search" />
                  <DateRangeFilter
                    dateFrom={crDateFrom}
                    dateTo={crDateTo}
                    onChange={({ dateFrom: f, dateTo: t }) => { setCrDateFrom(f); setCrDateTo(t); }}
                    color="indigo"
                    testIdPrefix="cr-range"
                  />
                </div>

                {/* Résumé compact */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {(() => {
                const fDebits = applySearch(applyDateRange(debits, crDateFrom, crDateTo, 'date').filter(d => !d.exclude_from_cr), crSearch);
                const fCredits = applySearch(applyDateRange(credits, crDateFrom, crDateTo, 'date').filter(c => !c.exclude_from_cr), crSearch);
                const tD = fDebits.reduce((s, d) => s + (parseFloat(d.montant) || 0), 0);
                const tC = fCredits.reduce((s, c) => s + (parseFloat(c.montant) || 0), 0);
                const net = tC - tD;
                return (
                  <>
                    <Card className="border-l-4 border-l-red-500 bg-red-50">
                      <CardContent className="p-4">
                        <p className="text-xs text-red-700 font-semibold uppercase">Total Débit</p>
                        <p className="text-2xl font-bold text-red-700">-{tD.toLocaleString()} FCFA</p>
                        <p className="text-xs text-gray-600 mt-1">{fDebits.length} transaction(s)</p>
                      </CardContent>
                    </Card>
                    <Card className="border-l-4 border-l-green-500 bg-green-50">
                      <CardContent className="p-4">
                        <p className="text-xs text-green-700 font-semibold uppercase">Total Crédit</p>
                        <p className="text-2xl font-bold text-green-700">+{tC.toLocaleString()} FCFA</p>
                        <p className="text-xs text-gray-600 mt-1">{fCredits.length} transaction(s)</p>
                      </CardContent>
                    </Card>
                    <Card className="border-l-4 border-l-blue-500 bg-blue-50">
                      <CardContent className="p-4">
                        <p className="text-xs text-blue-700 font-semibold uppercase">Solde Net</p>
                        <p className={`text-2xl font-bold ${net >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                          {net.toLocaleString()} FCFA
                        </p>
                      </CardContent>
                    </Card>
                  </>
                );
              })()}
            </div>

            {/* ==== COMPTE DE RÉSULTAT (format bilatéral : Charges | Produits) ==== */}
            {(() => {
              const crCredits = applySearch(applyDateRange(credits, crDateFrom, crDateTo, 'date').filter(c => !c.exclude_from_cr), crSearch);
              const crDebits = applySearch(applyDateRange(debits, crDateFrom, crDateTo, 'date').filter(d => !d.exclude_from_cr), crSearch);
              // Pour le calcul du Résultat Net : on exclut les apports/dividendes (= capitaux, hors exploitation)
              // conformément aux normes OHADA. Ces lignes restent affichées en lecture seule.
              const crCreditsOp = crCredits;
              const crDebitsOp = crDebits;
              const totalProduitsCR = crCreditsOp.reduce((s, c) => s + (parseFloat(c.montant) || 0), 0);
              const totalChargesCR = crDebitsOp.reduce((s, d) => s + (parseFloat(d.montant) || 0), 0);
              const resultatNetCR = totalProduitsCR - totalChargesCR;
              const isPositifCR = resultatNetCR >= 0;
              const formatCR = (n) => n.toLocaleString('fr-FR');
              const crPeriodLabel = (crDateFrom || crDateTo)
                ? `${crDateFrom ? formatDateFR(crDateFrom) : '—'} → ${crDateTo ? formatDateFR(crDateTo) : '—'}`
                : '';

              // Préparer les entrées avec label (libellé détaillé pour classification)
              const chargeEntriesForClassif = crDebits.map(d => ({
                label: `${d.categorie || ''} ${d.description || ''}`,
                amount: parseFloat(d.montant) || 0,
                original: d
              }));
              const produitEntriesForClassif = crCredits.map(c => ({
                label: `${c.annonceur || ''} ${c.description || ''}`,
                amount: parseFloat(c.montant) || 0,
                original: c
              }));
              const chargesGrouped = groupByAccountingClass(chargeEntriesForClassif, CHARGE_CLASSES);
              const produitsGrouped = groupByAccountingClass(produitEntriesForClassif, PRODUIT_CLASSES);

              return (
                <Card className="shadow-lg border-2 border-indigo-100" data-testid="compte-resultat-admin">
                  <CardHeader className="bg-gradient-to-r from-indigo-600 to-indigo-700 text-white">
                    <CardTitle className="flex items-center justify-between flex-wrap gap-2">
                      <span className="flex items-center gap-2">
                        <FileBarChart className="h-6 w-6" />
                        Compte de Résultat{crPeriodLabel ? '' : ` - ${new Date().toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })}`}
                      </span>
                      {crPeriodLabel && (
                        <span className="text-sm font-normal opacity-95" data-testid="cr-period-label">
                          Période : {crPeriodLabel}
                        </span>
                      )}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    <div className="grid grid-cols-1 md:grid-cols-2">
                      {/* CHARGES À GAUCHE */}
                      <div className="border-r border-gray-200">
                        <div className="bg-red-600 text-white px-6 py-3">
                          <h3 className="font-bold text-lg uppercase tracking-wide">Charges (Débit)</h3>
                        </div>
                        <table className="w-full">
                          <thead className="bg-red-50 border-b border-red-200">
                            <tr>
                              <th className="px-6 py-2 text-left text-xs font-semibold text-red-800 uppercase tracking-wider">Libellé</th>
                              <th className="px-6 py-2 text-right text-xs font-semibold text-red-800 uppercase tracking-wider">Montant (FCFA)</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-red-100">
                            {chargesGrouped.length === 0 ? (
                              <tr>
                                <td className="px-6 py-3 text-gray-400 italic">Aucune charge enregistrée</td>
                                <td className="px-6 py-3 text-right text-gray-400">0</td>
                              </tr>
                            ) : (
                              chargesGrouped.map(group => (
                                <React.Fragment key={`ch-${group.code}`}>
                                  {/* En-tête de classe comptable */}
                                  <tr className="bg-red-100/70">
                                    <td colSpan={2} className="px-4 py-2 text-xs font-bold text-red-900 uppercase tracking-wide">
                                      {group.code} - {group.label}
                                    </td>
                                  </tr>
                                  {/* Lignes individuelles */}
                                  {group.entries.map((e, i) => (
                                    <tr key={`ch-${group.code}-${i}`} className="hover:bg-red-50/50">
                                      <td className="px-6 py-2 pl-10 text-gray-700 text-sm">
                                        {e.original?.date && <span className="text-gray-900 font-mono text-xs mr-2">{formatDateFR(e.original.date)}</span>}
                                        {e.original?.libelle
                                          ? e.original.libelle
                                          : (<>
                                              {e.original?.categorie || 'Divers'}
                                              {e.original?.description && <span className="text-gray-500 text-xs ml-2">({e.original.description})</span>}
                                            </>)}
                                      </td>
                                      <td className="px-6 py-2 text-right text-red-700 font-medium">{formatCR(e.amount)}</td>
                                    </tr>
                                  ))}
                                </React.Fragment>
                              ))
                            )}
                            {/* Ligne résultat si bénéfice (équilibrage côté charges) */}
                            {isPositifCR && resultatNetCR > 0 && (
                              <tr className="bg-blue-50 border-t border-blue-200">
                                <td className="px-6 py-3 italic text-blue-700 font-medium">Résultat (Bénéfice)</td>
                                <td className="px-6 py-3 text-right text-blue-700 font-bold">{formatCR(resultatNetCR)}</td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>

                      {/* PRODUITS À DROITE */}
                      <div>
                        <div className="bg-green-600 text-white px-6 py-3">
                          <h3 className="font-bold text-lg uppercase tracking-wide">Produits (Crédit)</h3>
                        </div>
                        <table className="w-full">
                          <thead className="bg-green-50 border-b border-green-200">
                            <tr>
                              <th className="px-6 py-2 text-left text-xs font-semibold text-green-800 uppercase tracking-wider">Libellé</th>
                              <th className="px-6 py-2 text-right text-xs font-semibold text-green-800 uppercase tracking-wider">Montant (FCFA)</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-green-100">
                            {produitsGrouped.length === 0 ? (
                              <tr>
                                <td className="px-6 py-3 text-gray-400 italic">Aucun produit enregistré</td>
                                <td className="px-6 py-3 text-right text-gray-400">0</td>
                              </tr>
                            ) : (
                              produitsGrouped.map(group => (
                                <React.Fragment key={`p-${group.code}`}>
                                  {/* En-tête de classe comptable */}
                                  <tr className="bg-green-100/70">
                                    <td colSpan={2} className="px-4 py-2 text-xs font-bold text-green-900 uppercase tracking-wide">
                                      {group.code} - {group.label}
                                    </td>
                                  </tr>
                                  {/* Lignes individuelles */}
                                  {group.entries.map((e, i) => (
                                    <tr key={`p-${group.code}-${i}`} className="hover:bg-green-50/50">
                                      <td className="px-6 py-2 pl-10 text-gray-700 text-sm">
                                        {e.original?.date && <span className="text-gray-900 font-mono text-xs mr-2">{formatDateFR(e.original.date)}</span>}
                                        {e.original?.libelle
                                          ? e.original.libelle
                                          : (<>
                                              {e.original?.annonceur || 'Divers'}
                                              {e.original?.description && <span className="text-gray-500 text-xs ml-2">({e.original.description})</span>}
                                            </>)}
                                      </td>
                                      <td className="px-6 py-2 text-right text-green-700 font-medium">{formatCR(e.amount)}</td>
                                    </tr>
                                  ))}
                                </React.Fragment>
                              ))
                            )}
                            {/* Ligne résultat si perte (équilibrage côté produits) */}
                            {!isPositifCR && resultatNetCR < 0 && (
                              <tr className="bg-orange-50 border-t border-orange-200">
                                <td className="px-6 py-3 italic text-orange-700 font-medium">Résultat (Perte)</td>
                                <td className="px-6 py-3 text-right text-orange-700 font-bold">{formatCR(Math.abs(resultatNetCR))}</td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    {/* Ligne unique TOTAL CHARGES | TOTAL PRODUITS */}
                    <div className="grid grid-cols-1 md:grid-cols-2 bg-gradient-to-r from-red-100 via-gray-100 to-green-100 border-t-2 border-b-2 border-gray-300 font-bold">
                      <div className="px-6 py-4 flex items-center justify-between border-r border-gray-300">
                        <span className="text-red-900 uppercase tracking-wide">Total Charges</span>
                        <span className="text-red-900 text-xl" data-testid="total-charges-admin">
                          {formatCR(totalChargesCR + (isPositifCR ? resultatNetCR : 0))} FCFA
                        </span>
                      </div>
                      <div className="px-6 py-4 flex items-center justify-between">
                        <span className="text-green-900 uppercase tracking-wide">Total Produits</span>
                        <span className="text-green-900 text-xl" data-testid="total-produits-admin">
                          {formatCR(totalProduitsCR + (!isPositifCR ? Math.abs(resultatNetCR) : 0))} FCFA
                        </span>
                      </div>
                    </div>

                    {/* Bandeau Résultat Net global */}
                    <div className={`px-6 py-4 border-t-4 ${isPositifCR ? 'bg-blue-100 border-blue-400' : 'bg-orange-100 border-orange-400'}`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Equal className="h-5 w-5" />
                          <span className="font-bold text-lg uppercase tracking-wide">
                            Résultat Net {isPositifCR ? '(Bénéfice)' : '(Perte)'}
                          </span>
                        </div>
                        <span className={`font-bold text-2xl ${isPositifCR ? 'text-blue-800' : 'text-orange-800'}`} data-testid="resultat-net-admin">
                          {isPositifCR ? '+' : ''}{formatCR(resultatNetCR)} FCFA
                        </span>
                      </div>
                    </div>

                    {/* Footer ratios */}
                    <div className="p-4 bg-gray-50 border-t grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                      <div>
                        <div className="text-xs text-gray-500 uppercase">Taux de marge nette</div>
                        <div className="font-bold text-gray-800 text-lg">
                          {totalProduitsCR > 0 ? ((resultatNetCR / totalProduitsCR) * 100).toFixed(1) : '0'}%
                        </div>
                      </div>
                      <div>
                        <div className="text-xs text-gray-500 uppercase">Ratio Charges / Produits</div>
                        <div className="font-bold text-gray-800 text-lg">
                          {totalProduitsCR > 0 ? ((totalChargesCR / totalProduitsCR) * 100).toFixed(1) : '0'}%
                        </div>
                      </div>
                      <div>
                        <div className="text-xs text-gray-500 uppercase">Transactions totales</div>
                        <div className="font-bold text-gray-800 text-lg">{credits.length + debits.length}</div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })()}

            {/* TABLEAU UNIFIÉ DES CHARGES (saisies enregistrées + nouvelles lignes à saisir) */}
            <Card className="border-2 border-red-200">
              <CardHeader className="bg-red-50 flex flex-row items-center justify-between flex-wrap gap-2">
                <CardTitle className="text-red-700 text-lg">
                  Tableau des Charges ({applyPeriodFilter(debits, chargesPeriod).length}/{debits.length})
                </CardTitle>
                <div className="flex items-center gap-2 flex-wrap">
                  <PeriodFilter value={chargesPeriod} onChange={setChargesPeriod} color="red" testIdPrefix="charges-period" />
                  <Button size="sm" variant="outline" onClick={addChargeRow} className="gap-1" data-testid="add-charge-row-btn">
                    <Plus className="h-3 w-3" /> Ligne
                  </Button>
                  <Button size="sm" onClick={saveChargesToDebits} className="bg-red-600 hover:bg-red-700 gap-1" data-testid="save-charges-btn">
                    <Save className="h-3 w-3" /> Enregistrer
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-red-100 text-red-900">
                      <tr>
                        <th className="px-3 py-2 text-left font-semibold">Date</th>
                        <th className="px-3 py-2 text-left font-semibold">Catégorie</th>
                        <th className="px-3 py-2 text-left font-semibold">Description</th>
                        <th className="px-3 py-2 text-left font-semibold">Statut</th>
                        <th className="px-3 py-2 text-right font-semibold">Montant (FCFA)</th>
                        <th className="px-3 py-2 w-20 text-center font-semibold">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {/* Empty state */}
                      {applySearch(applyPeriodFilter(debits, chargesPeriod), crSearch).length === 0 && chargesTable.length === 0 && (
                        <tr>
                          <td colSpan="6" className="text-center text-gray-400 py-6 text-sm">Aucune charge {chargesPeriod === 'all' ? 'enregistrée' : 'pour cette période'}</td>
                        </tr>
                      )}

                      {/* Lignes à saisir (éditables) — affichées EN PREMIER */}
                      {chargesTable.map((row) => (
                        <tr key={`draft-${row.id}`} className="border-b bg-red-50/30 hover:bg-red-50">
                          <td className="px-2 py-1">
                            <DatePickerField
                              value={row.date}
                              onChange={(v) => updateChargeRow(row.id, 'date', v)}
                              size="sm"
                              className="border-red-200"
                            />
                          </td>
                          <td className="px-2 py-1">
                            <CategorySelect
                              value={row.categorie}
                              onChange={(v) => updateChargeRow(row.id, 'categorie', v)}
                              type="charge"
                              defaultCategories={CHARGE_CATEGORIES}
                              hiddenDefaults={hiddenChargeDefaults}
                              customCategories={customChargeCats}
                              onCategoriesChange={onChargeCatsChange}
                              triggerClassName="h-8 border-red-200"
                              testId={`charge-row-categorie-${row.id}`}
                            />
                          </td>
                          <td className="px-2 py-1">
                            <Input
                              value={row.description}
                              onChange={(e) => updateChargeRow(row.id, 'description', e.target.value)}
                              placeholder="Description..."
                              className="h-8 border-red-200"
                            />
                          </td>
                          <td className="px-2 py-1">
                            <Select value={row.statut} onValueChange={(v) => updateChargeRow(row.id, 'statut', v)}>
                              <SelectTrigger className="h-8 border-red-200">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="En attente">En attente</SelectItem>
                                <SelectItem value="Payé">Payé</SelectItem>
                                <SelectItem value="Annulé">Annulé</SelectItem>
                              </SelectContent>
                            </Select>
                          </td>
                          <td className="px-2 py-1">
                            <Input
                              type="number"
                              value={row.montant}
                              onChange={(e) => updateChargeRow(row.id, 'montant', parseFloat(e.target.value) || 0)}
                              className="h-8 border-red-200 text-right font-semibold text-red-700"
                              min="0"
                              step="0.01"
                            />
                          </td>
                          <td className="px-2 py-1">
                            <div className="flex items-center justify-center">
                              <Button size="sm" variant="ghost" onClick={() => removeChargeRow(row.id)} className="h-8 w-8 p-0 text-red-500 hover:bg-red-100" title="Retirer la ligne">
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}

                      {/* Séparateur visuel entre lignes à saisir et lignes enregistrées */}
                      {applySearch(applyPeriodFilter(debits, chargesPeriod), crSearch).length > 0 && chargesTable.length > 0 && (
                        <tr className="bg-red-50/60">
                          <td colSpan="6" className="px-3 py-1 text-[11px] uppercase tracking-wider font-semibold text-red-700 border-t border-b border-red-200">
                            Charges déjà enregistrées
                          </td>
                        </tr>
                      )}

                      {/* Transactions enregistrées (read-only + edit/delete) */}
                      {applySearch(applyPeriodFilter(debits, chargesPeriod), crSearch).map(debit => (
                        <tr key={`saved-${debit.id}`} className="border-b bg-white hover:bg-red-50/60">
                          <td className="px-3 py-2 whitespace-nowrap">{new Date(debit.date).toLocaleDateString('fr-FR')}</td>
                          <td className="px-3 py-2">
                            <Badge className="bg-red-500 text-white text-xs">{debit.categorie}</Badge>
                          </td>
                          <td className="px-3 py-2 font-medium text-gray-800">{debit.description}</td>
                          <td className="px-3 py-2">
                            <Badge variant="outline" className="border-red-300 text-red-700 text-xs">{debit.statut}</Badge>
                          </td>
                          <td className="px-3 py-2 text-right font-bold text-red-600 whitespace-nowrap">
                            -{(parseFloat(debit.montant) || 0).toLocaleString()} FCFA
                          </td>
                          <td className="px-2 py-2">
                            <div className="flex items-center justify-center gap-1">
                              <Button
                                size="sm"
                                variant="ghost"
                                type="button"
                                className="h-8 w-8 p-0 text-blue-600 hover:bg-blue-100"
                                onClick={() => handleEditDebit(debit)}
                                data-testid={`edit-debit-${debit.id}`}
                                title="Modifier"
                              >
                                <Edit2 className="h-4 w-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                type="button"
                                className="h-8 w-8 p-0 text-red-600 hover:bg-red-100"
                                onClick={() => handleDeleteDebit(debit)}
                                data-testid={`delete-debit-${debit.id}`}
                                title="Supprimer"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="bg-red-100 font-semibold">
                      <tr>
                        <td colSpan="4" className="px-3 py-2 text-right text-red-900">TOTAL CHARGES :</td>
                        <td className="px-3 py-2 text-right text-red-700 text-base">
                          {(
                            applySearch(applyPeriodFilter(debits, chargesPeriod), crSearch).reduce((s, d) => s + (parseFloat(d.montant) || 0), 0) +
                            chargesTable.reduce((s, r) => s + (parseFloat(r.montant) || 0), 0)
                          ).toLocaleString()} FCFA
                        </td>
                        <td></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </CardContent>
            </Card>

            {/* TABLEAU UNIFIÉ DES PRODUITS (saisies enregistrées + nouvelles lignes à saisir) */}
            <Card className="border-2 border-green-200">
              <CardHeader className="bg-green-50 flex flex-row items-center justify-between flex-wrap gap-2">
                <CardTitle className="text-green-700 text-lg">
                  Tableau des Produits ({applySearch(applyPeriodFilter(credits, produitsPeriod), crSearch).length}/{credits.length})
                </CardTitle>
                <div className="flex items-center gap-2 flex-wrap">
                  <PeriodFilter value={produitsPeriod} onChange={setProduitsPeriod} color="green" testIdPrefix="produits-period" />
                  <Button size="sm" variant="outline" type="button" onClick={addProduitRow} className="gap-1" data-testid="add-produit-row-btn">
                    <Plus className="h-4 w-4" /> Ligne
                  </Button>
                  <Button size="sm" type="button" onClick={saveProduitsToCredits} className="bg-green-600 hover:bg-green-700 gap-1" data-testid="save-produits-btn">
                    <Save className="h-4 w-4" /> Enregistrer
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-green-100 text-green-900">
                      <tr>
                        <th className="px-3 py-2 text-left font-semibold">Date</th>
                        <th className="px-3 py-2 text-left font-semibold">Catégorie</th>
                        <th className="px-3 py-2 text-left font-semibold">Description</th>
                        <th className="px-3 py-2 text-right font-semibold">Montant (FCFA)</th>
                        <th className="px-3 py-2 w-20 text-center font-semibold">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {/* Empty state */}
                      {applySearch(applyPeriodFilter(credits, produitsPeriod), crSearch).length === 0 && produitsTable.length === 0 && (
                        <tr>
                          <td colSpan="5" className="text-center text-gray-400 py-6 text-sm">Aucun produit {produitsPeriod === 'all' ? 'enregistré' : 'pour cette période'}</td>
                        </tr>
                      )}

                      {/* Lignes à saisir (éditables) — affichées EN PREMIER */}
                      {produitsTable.map((row) => (
                        <tr key={`draft-${row.id}`} className="border-b bg-green-50/30 hover:bg-green-50">
                          <td className="px-2 py-1">
                            <DatePickerField
                              value={row.date}
                              onChange={(v) => updateProduitRow(row.id, 'date', v)}
                              size="sm"
                              className="border-green-200"
                              testId={`produit-row-date-${row.id}`}
                            />
                          </td>
                          <td className="px-2 py-1">
                            <CategorySelect
                              value={row.categorie || 'Location panneau (mensuel)'}
                              onChange={(v) => updateProduitRow(row.id, 'categorie', v)}
                              type="produit"
                              defaultCategories={PRODUIT_CATEGORIES}
                              hiddenDefaults={hiddenProduitDefaults}
                              customCategories={customProduitCats}
                              onCategoriesChange={onProduitCatsChange}
                              triggerClassName="h-8 border-green-200"
                              testId={`produit-row-categorie-${row.id}`}
                            />
                          </td>
                          <td className="px-2 py-1">
                            <Input
                              value={row.description}
                              onChange={(e) => updateProduitRow(row.id, 'description', e.target.value)}
                              placeholder="Description..."
                              className="h-8 border-green-200"
                              data-testid={`produit-row-description-${row.id}`}
                            />
                          </td>
                          <td className="px-2 py-1">
                            <Input
                              type="number"
                              value={row.montant}
                              onChange={(e) => updateProduitRow(row.id, 'montant', parseFloat(e.target.value) || 0)}
                              className="h-8 border-green-200 text-right font-semibold text-green-700"
                              data-testid={`produit-row-montant-${row.id}`}
                            />
                          </td>
                          <td className="px-2 py-1">
                            <div className="flex items-center justify-center">
                              {produitsTable.length > 1 && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  type="button"
                                  onClick={() => removeProduitRow(row.id)}
                                  className="h-8 w-8 p-0 text-red-500 hover:bg-red-100"
                                  title="Retirer la ligne"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}

                      {/* Séparateur visuel entre lignes à saisir et lignes enregistrées */}
                      {applySearch(applyPeriodFilter(credits, produitsPeriod), crSearch).length > 0 && produitsTable.length > 0 && (
                        <tr className="bg-green-50/60">
                          <td colSpan="5" className="px-3 py-1 text-[11px] uppercase tracking-wider font-semibold text-green-700 border-t border-b border-green-200">
                            Produits déjà enregistrés
                          </td>
                        </tr>
                      )}

                      {/* Transactions enregistrées (read-only + edit/delete) */}
                      {applySearch(applyPeriodFilter(credits, produitsPeriod), crSearch).map(credit => (
                        <tr key={`saved-${credit.id}`} className="border-b bg-white hover:bg-green-50/60">
                          <td className="px-3 py-2 whitespace-nowrap">{new Date(credit.date).toLocaleDateString('fr-FR')}</td>
                          <td className="px-3 py-2">
                            {credit.categorie && <Badge className="bg-green-500 text-white text-xs">{credit.categorie}</Badge>}
                          </td>
                          <td className="px-3 py-2 font-medium text-gray-800">{credit.description}</td>
                          <td className="px-3 py-2 text-right font-bold text-green-600 whitespace-nowrap">
                            +{(parseFloat(credit.montant) || 0).toLocaleString()} FCFA
                          </td>
                          <td className="px-2 py-2">
                            <div className="flex items-center justify-center gap-1">
                              <Button
                                size="sm"
                                variant="ghost"
                                type="button"
                                className="h-8 w-8 p-0 text-blue-600 hover:bg-blue-100"
                                onClick={() => handleEditCredit(credit)}
                                data-testid={`edit-credit-${credit.id}`}
                                title="Modifier"
                              >
                                <Edit2 className="h-4 w-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                type="button"
                                className="h-8 w-8 p-0 text-red-600 hover:bg-red-100"
                                onClick={() => handleDeleteCredit(credit)}
                                data-testid={`delete-credit-${credit.id}`}
                                title="Supprimer"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="bg-green-100 font-semibold">
                      <tr>
                        <td colSpan="3" className="px-3 py-2 text-right text-green-900">TOTAL PRODUITS :</td>
                        <td className="px-3 py-2 text-right text-green-700 text-base">
                          {(
                            applySearch(applyPeriodFilter(credits, produitsPeriod), crSearch).reduce((s, c) => s + (parseFloat(c.montant) || 0), 0) +
                            produitsTable.reduce((s, r) => s + (parseFloat(r.montant) || 0), 0)
                          ).toLocaleString()} FCFA
                        </td>
                        <td></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </CardContent>
            </Card>
              </>
            )}
          </TabsContent>

          {/* INVESTISSEURS (onglet dédié, à côté de Comptabilité) */}
          <TabsContent value="investisseurs" className="space-y-6">
            <InvestisseursPanel hideBackButton />
          </TabsContent>

          {/* CERTIFICATIONS */}
          <TabsContent value="certifications" className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-2xl font-bold">Certifications & Récompenses</h2>
              <Button onClick={() => setShowCertificationModal(true)} className="gap-2">
                <Plus className="h-4 w-4" />
                Ajouter
              </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {certifications.map(cert => (
                <Card key={cert.id} className="border-2 border-yellow-200 hover:shadow-lg transition-shadow">
                  <CardContent className="p-6">
                    <div className="text-center">
                      {cert.logo_url ? (
                        <img src={cert.logo_url} alt={cert.name} className="w-24 h-24 mx-auto mb-4 object-contain" />
                      ) : (
                        <div className="w-24 h-24 mx-auto mb-4 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full flex items-center justify-center">
                          <Award className="h-12 w-12 text-white" />
                        </div>
                      )}
                      
                      <h3 className="font-bold text-lg mb-2">{cert.name}</h3>
                      
                      <Badge className={
                        cert.type === 'award' ? 'bg-yellow-500' :
                        cert.type === 'partnership' ? 'bg-blue-500' :
                        'bg-green-500'
                      }>
                        {cert.type === 'award' ? 'Récompense' :
                         cert.type === 'partnership' ? 'Partenariat' :
                         'Certification'}
                      </Badge>
                      
                      {cert.issuer && (
                        <p className="text-sm text-gray-600 mt-2">Délivré par: {cert.issuer}</p>
                      )}
                      
                      {cert.description && (
                        <p className="text-sm text-gray-600 mt-2">{cert.description}</p>
                      )}
                      
                      {cert.obtained_date && (
                        <p className="text-xs text-gray-500 mt-3">
                          Obtenu le {new Date(cert.obtained_date).toLocaleDateString('fr-FR')}
                        </p>
                      )}
                      
                      {cert.certificate_url && (
                        <Button 
                          size="sm" 
                          variant="outline" 
                          className="mt-3"
                          onClick={() => window.open(cert.certificate_url, '_blank')}
                        >
                          Voir le certificat
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {certifications.length === 0 && (
              <Card className="border-2 border-dashed border-gray-300">
                <CardContent className="p-12 text-center">
                  <Award className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                  <p className="text-gray-600 mb-4">Aucune certification enregistrée</p>
                  <Button onClick={() => setShowCertificationModal(true)}>
                    Ajouter la première certification
                  </Button>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* REVENUS */}
          <TabsContent value="revenus" className="space-y-6">
            <Revenus user={user} onLogout={onLogout} embedded={true} />
          </TabsContent>

          {/* RAPPORTS */}
          <TabsContent value="reports" className="space-y-6">
            <h2 className="text-2xl font-bold">Rapports & Analytics</h2>
            
            <Card>
              <CardHeader>
                <CardTitle>Indicateurs clés de performance (KPIs)</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Chiffre d'affaires mensuel</p>
                    <p className="text-2xl font-bold">{formatCurrency(analytics?.kpis?.monthly_revenue || 0)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Dépenses mensuelles</p>
                    <p className="text-2xl font-bold text-red-600">{formatCurrency(analytics?.kpis?.monthly_expenses || 0)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Bénéfice net</p>
                    <p className={`text-2xl font-bold ${(analytics?.kpis?.net_profit || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {formatCurrency(analytics?.kpis?.net_profit || 0)}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Taux de conversion</p>
                    <p className="text-2xl font-bold">{analytics?.kpis?.conversion_rate || 0}%</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Contrats actifs</p>
                    <p className="text-2xl font-bold">{analytics?.kpis?.active_contracts || 0}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Revenu par employé</p>
                    <p className="text-2xl font-bold">{formatCurrency(analytics?.kpis?.revenue_per_employee || 0)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Actions rapides</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4">
                  <Button variant="outline" onClick={handleExportExpenses} className="gap-2">
                    <Download className="h-4 w-4" />
                    Exporter les dépenses
                  </Button>
                  <Button variant="outline" onClick={async () => {
                    try {
                      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/company/export/employees`);
                      const dataStr = JSON.stringify(response.data, null, 2);
                      const dataBlob = new Blob([dataStr], { type: 'application/json' });
                      const url = URL.createObjectURL(dataBlob);
                      const link = document.createElement('a');
                      link.href = url;
                      link.download = `employes_${new Date().toISOString().split('T')[0]}.json`;
                      link.click();
                      toast.success('Export réussi');
                    } catch (error) {
                      toast.error('Erreur lors de l\'export');
                    }
                  }} className="gap-2">
                    <Download className="h-4 w-4" />
                    Exporter les employés
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* MODALS */}

      {/* Modal Document */}
      <Dialog open={showDocumentModal} onOpenChange={setShowDocumentModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Enregistrer un document</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label>Nom du document *</Label>
              <Input
                value={newDocument.name}
                onChange={(e) => setNewDocument({...newDocument, name: e.target.value})}
                placeholder="Ex: Contrat fournisseur XYZ"
              />
            </div>
            <div>
              <Label>Catégorie</Label>
              <Select value={newDocument.category} onValueChange={(val) => setNewDocument({...newDocument, category: val})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Administratif">Administratif</SelectItem>
                  <SelectItem value="Juridique">Juridique</SelectItem>
                  <SelectItem value="Commercial">Commercial</SelectItem>
                  <SelectItem value="Financier">Financier</SelectItem>
                  <SelectItem value="Autre">Autre</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>URL du fichier</Label>
              <Input
                value={newDocument.file_url}
                onChange={(e) => setNewDocument({...newDocument, file_url: e.target.value})}
                placeholder="https://..."
              />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea
                value={newDocument.description}
                onChange={(e) => setNewDocument({...newDocument, description: e.target.value})}
                placeholder="Détails du document"
                rows={3}
              />
            </div>
            <div>
              <Label>Date d'expiration (optionnel)</Label>
              <Input
                type="date"
                value={newDocument.expiry_date}
                onChange={(e) => setNewDocument({...newDocument, expiry_date: e.target.value})}
              />
            </div>
            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button variant="outline" onClick={() => setShowDocumentModal(false)}>Annuler</Button>
              <Button onClick={handleCreateDocument}>Enregistrer</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal Dépense */}
      <Dialog open={showExpenseModal} onOpenChange={setShowExpenseModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Enregistrer une dépense</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label>Titre *</Label>
              <Input
                value={newExpense.title}
                onChange={(e) => setNewExpense({...newExpense, title: e.target.value})}
                placeholder="Ex: Achat matériel informatique"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Montant (FCFA) *</Label>
                <Input
                  type="number"
                  value={newExpense.amount}
                  onChange={(e) => setNewExpense({...newExpense, amount: parseFloat(e.target.value) || 0})}
                />
              </div>
              <div>
                <Label>Catégorie</Label>
                <Select value={newExpense.category} onValueChange={(val) => setNewExpense({...newExpense, category: val})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Salaires">Salaires</SelectItem>
                    <SelectItem value="Loyer">Loyer</SelectItem>
                    <SelectItem value="Électricité">Électricité</SelectItem>
                    <SelectItem value="Matériel">Matériel</SelectItem>
                    <SelectItem value="Marketing">Marketing</SelectItem>
                    <SelectItem value="Autre">Autre</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Date</Label>
              <Input
                type="date"
                value={newExpense.date}
                onChange={(e) => setNewExpense({...newExpense, date: e.target.value})}
              />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea
                value={newExpense.description}
                onChange={(e) => setNewExpense({...newExpense, description: e.target.value})}
                placeholder="Détails de la dépense"
                rows={3}
              />
            </div>
            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button variant="outline" onClick={() => setShowExpenseModal(false)}>Annuler</Button>
              <Button onClick={handleCreateExpense}>Enregistrer</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal Certification */}
      <Dialog open={showCertificationModal} onOpenChange={setShowCertificationModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Ajouter une certification</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label>Nom *</Label>
              <Input
                value={newCertification.name}
                onChange={(e) => setNewCertification({...newCertification, name: e.target.value})}
                placeholder="Ex: Certification ISO 9001"
              />
            </div>
            <div>
              <Label>Type</Label>
              <Select value={newCertification.type} onValueChange={(val) => setNewCertification({...newCertification, type: val})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="certification">Certification</SelectItem>
                  <SelectItem value="award">Récompense</SelectItem>
                  <SelectItem value="partnership">Partenariat</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Émetteur</Label>
              <Input
                value={newCertification.issuer}
                onChange={(e) => setNewCertification({...newCertification, issuer: e.target.value})}
                placeholder="Ex: ISO, Google, Microsoft..."
              />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea
                value={newCertification.description}
                onChange={(e) => setNewCertification({...newCertification, description: e.target.value})}
                rows={3}
              />
            </div>
            <div>
              <Label>Date d'obtention</Label>
              <Input
                type="date"
                value={newCertification.obtained_date}
                onChange={(e) => setNewCertification({...newCertification, obtained_date: e.target.value})}
              />
            </div>
            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button variant="outline" onClick={() => setShowCertificationModal(false)}>Annuler</Button>
              <Button onClick={handleCreateCertification}>Ajouter</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal Employé (création ou édition) */}
      <Dialog
        open={showEmployeeModal}
        onOpenChange={(v) => {
          setShowEmployeeModal(v);
          if (!v) {
            setEditingEmployeeId(null);
            setNewEmployee({ nom: '', prenom: '', poste: '', departement: 'Général', email: '', telephone: '' });
          }
        }}
      >
        <DialogContent className="max-w-2xl" data-testid="employee-modal">
          <DialogHeader>
            <DialogTitle>
              {editingEmployeeId ? "Modifier le membre" : "Ajouter un membre de l'équipe"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Nom *</Label>
                <Input
                  value={newEmployee.nom}
                  onChange={(e) => setNewEmployee({...newEmployee, nom: e.target.value})}
                  placeholder="Nom de famille"
                />
              </div>
              <div>
                <Label>Prénom *</Label>
                <Input
                  value={newEmployee.prenom}
                  onChange={(e) => setNewEmployee({...newEmployee, prenom: e.target.value})}
                  placeholder="Prénom"
                />
              </div>
            </div>
            <div>
              <Label>Poste *</Label>
              <Select value={newEmployee.poste} onValueChange={(val) => setNewEmployee({...newEmployee, poste: val})}>
                <SelectTrigger>
                  <SelectValue placeholder="Sélectionnez un poste" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Président">Président</SelectItem>
                  <SelectItem value="Directeur">Directeur</SelectItem>
                  <SelectItem value="Associé">Associé</SelectItem>
                  <SelectItem value="Responsable Commercial">Responsable Commercial</SelectItem>
                  <SelectItem value="Coordonnateur">Coordonnateur</SelectItem>
                  <SelectItem value="Commerciale">Commerciale</SelectItem>
                  <SelectItem value="Technicien">Technicien</SelectItem>
                  <SelectItem value="Assistant">Assistant</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Département</Label>
              <Select value={newEmployee.departement} onValueChange={(val) => setNewEmployee({...newEmployee, departement: val})}>
                <SelectTrigger>
                  <SelectValue placeholder="Sélectionnez un département" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Général">Général</SelectItem>
                  <SelectItem value="Commercial">Commercial</SelectItem>
                  <SelectItem value="Technique">Technique</SelectItem>
                  <SelectItem value="Administration">Administration</SelectItem>
                  <SelectItem value="Marketing">Marketing</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Email</Label>
              <Input
                type="email"
                value={newEmployee.email}
                onChange={(e) => setNewEmployee({...newEmployee, email: e.target.value})}
                placeholder="email@example.com"
              />
            </div>
            <div>
              <Label>Téléphone</Label>
              <Input
                type="tel"
                value={newEmployee.telephone}
                onChange={(e) => setNewEmployee({...newEmployee, telephone: e.target.value})}
                placeholder="+224 XXX XXX XXX"
              />
            </div>
            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button variant="outline" onClick={() => { setShowEmployeeModal(false); setEditingEmployeeId(null); }}>Annuler</Button>
              <Button
                onClick={editingEmployeeId ? handleUpdateEmployee : handleAddEmployee}
                className="bg-blue-600 hover:bg-blue-700"
                data-testid="save-employee-btn"
              >
                {editingEmployeeId ? (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    Enregistrer
                  </>
                ) : (
                  <>
                    <Plus className="h-4 w-4 mr-2" />
                    Ajouter
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal Débit */}
      <Dialog open={showDebitModal} onOpenChange={setShowDebitModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <TrendingUp className="h-5 w-5 rotate-180" />
              Enregistrer une Dépense (Débit)
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label>Date *</Label>
              <DatePickerField
                value={newDebit.date}
                onChange={(v) => setNewDebit({ ...newDebit, date: v })}
                testId="debit-date-picker"
              />
            </div>
            <div>
              <Label>Catégorie de Dépense *</Label>
              <CategorySelect
                value={newDebit.categorie}
                onChange={(val) => setNewDebit({...newDebit, categorie: val})}
                type="charge"
                defaultCategories={CHARGE_CATEGORIES}
                hiddenDefaults={hiddenChargeDefaults}
                customCategories={customChargeCats}
                onCategoriesChange={onChargeCatsChange}
                testId="new-debit-categorie"
              />
            </div>
            <div>
              <Label>Description *</Label>
              <Textarea
                value={newDebit.description}
                onChange={(e) => setNewDebit({...newDebit, description: e.target.value})}
                placeholder="Détails de la dépense..."
                rows={3}
              />
            </div>
            <div>
              <Label>Statut</Label>
              <Select value={newDebit.statut} onValueChange={(val) => setNewDebit({...newDebit, statut: val})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="En attente">En attente</SelectItem>
                  <SelectItem value="Payé">Payé</SelectItem>
                  <SelectItem value="Annulé">Annulé</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Montant (FCFA) *</Label>
              <Input
                type="number"
                value={newDebit.montant}
                onChange={(e) => setNewDebit({...newDebit, montant: parseFloat(e.target.value) || 0})}
                placeholder="0.00"
                min="0"
                step="0.01"
              />
            </div>
            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button variant="outline" onClick={() => setShowDebitModal(false)}>Annuler</Button>
              <Button 
                onClick={async () => {
                  if (newDebit.description && newDebit.montant > 0) {
                    try {
                      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/financial/debit`, newDebit);
                      await loadFinancialTransactions();
                      setNewDebit({ 
                        date: new Date().toISOString().split('T')[0], 
                        description: '', 
                        categorie: 'Loyer et charge',
                        statut: 'En attente', 
                        montant: 0 
                      });
                      setShowDebitModal(false);
                      toast.success('Débit enregistré (synchronisé avec les associés)');
                    } catch (error) {
                      toast.error('Erreur lors de l\'enregistrement');
                    }
                  } else {
                    toast.error('Veuillez remplir tous les champs requis');
                  }
                }}
                className="bg-red-600 hover:bg-red-700"
              >
                Enregistrer
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal Crédit */}
      <Dialog open={showCreditModal} onOpenChange={setShowCreditModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-green-700">
              <TrendingUp className="h-5 w-5" />
              Enregistrer un Revenu (Crédit)
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label>Date *</Label>
              <DatePickerField
                value={newCredit.date}
                onChange={(v) => setNewCredit({ ...newCredit, date: v })}
                testId="credit-date-picker"
              />
            </div>
            <div>
              <Label>Description *</Label>
              <Textarea
                value={newCredit.description}
                onChange={(e) => setNewCredit({...newCredit, description: e.target.value})}
                placeholder="Détails du revenu..."
                rows={3}
              />
            </div>
            <div>
              <Label>Annonceur *</Label>
              <Select value={newCredit.annonceur} onValueChange={(val) => setNewCredit({...newCredit, annonceur: val})}>
                <SelectTrigger>
                  <SelectValue placeholder="Sélectionner un annonceur" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="orange">Orange</SelectItem>
                  <SelectItem value="Telecel">Telecel</SelectItem>
                  <SelectItem value="Ecobank">Ecobank</SelectItem>
                  <SelectItem value="canal">Canal</SelectItem>
                  <SelectItem value="ledger">Ledger</SelectItem>
                  <SelectItem value="OMS">OMS</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Montant (FCFA) *</Label>
              <Input
                type="number"
                value={newCredit.montant}
                onChange={(e) => setNewCredit({...newCredit, montant: parseFloat(e.target.value) || 0})}
                placeholder="0.00"
                min="0"
                step="0.01"
              />
            </div>
            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button variant="outline" onClick={() => setShowCreditModal(false)}>Annuler</Button>
              <Button 
                onClick={async () => {
                  if (newCredit.description && newCredit.montant > 0) {
                    try {
                      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/financial/credit`, newCredit);
                      await loadFinancialTransactions();
                      setNewCredit({ date: new Date().toISOString().split('T')[0], description: '', annonceur: 'Orange', montant: 0 });
                      setShowCreditModal(false);
                      toast.success('Crédit enregistré (synchronisé avec les associés)');
                    } catch (error) {
                      toast.error('Erreur lors de l\'enregistrement');
                    }
                  } else {
                    toast.error('Veuillez remplir tous les champs requis');
                  }
                }}
                className="bg-green-600 hover:bg-green-700"
              >
                Enregistrer
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ===== Modal ÉDITION DÉBIT ===== */}
      <Dialog open={showEditDebitModal} onOpenChange={setShowEditDebitModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-blue-700">
              <Edit2 className="h-5 w-5" />
              Modifier le Débit (Correction d'erreur de saisie)
            </DialogTitle>
          </DialogHeader>
          {editingDebit && (
            <div className="space-y-4 mt-4">
              <div>
                <Label>Date</Label>
                <DatePickerField
                  value={editingDebit.date}
                  onChange={(v) => setEditingDebit({ ...editingDebit, date: v })}
                  className="mt-1"
                  testId="edit-debit-date"
                />
              </div>
              <div>
                <Label>Catégorie</Label>
                <CategorySelect
                  value={editingDebit.categorie}
                  onChange={(val) => setEditingDebit({ ...editingDebit, categorie: val })}
                  type="charge"
                  defaultCategories={CHARGE_CATEGORIES}
                  hiddenDefaults={hiddenChargeDefaults}
                  customCategories={customChargeCats}
                  onCategoriesChange={onChargeCatsChange}
                  testId="edit-debit-categorie"
                  triggerClassName="mt-1"
                />
              </div>
              <div>
                <Label>Description *</Label>
                <Textarea
                  value={editingDebit.description}
                  onChange={(e) => setEditingDebit({ ...editingDebit, description: e.target.value })}
                  rows={2}
                  className="mt-1"
                  data-testid="edit-debit-description"
                />
              </div>
              <div>
                <Label>Statut</Label>
                <Select
                  value={editingDebit.statut}
                  onValueChange={(val) => setEditingDebit({ ...editingDebit, statut: val })}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="En attente">En attente</SelectItem>
                    <SelectItem value="Payé">Payé</SelectItem>
                    <SelectItem value="Annulé">Annulé</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Montant (FCFA) *</Label>
                <Input
                  type="number"
                  value={editingDebit.montant}
                  onChange={(e) => setEditingDebit({ ...editingDebit, montant: parseFloat(e.target.value) || 0 })}
                  className="mt-1"
                  data-testid="edit-debit-montant"
                />
              </div>
              <div className="flex justify-end gap-2 pt-4 border-t">
                <Button variant="outline" onClick={() => setShowEditDebitModal(false)}>Annuler</Button>
                <Button
                  onClick={handleSaveEditDebit}
                  className="bg-blue-600 hover:bg-blue-700 gap-2"
                  data-testid="save-edit-debit-btn"
                >
                  <Save className="h-4 w-4" />
                  Enregistrer les corrections
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ===== Modal ÉDITION CRÉDIT ===== */}
      <Dialog open={showEditCreditModal} onOpenChange={setShowEditCreditModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-blue-700">
              <Edit2 className="h-5 w-5" />
              Modifier le Crédit (Correction d'erreur de saisie)
            </DialogTitle>
          </DialogHeader>
          {editingCredit && (
            <div className="space-y-4 mt-4">
              <div>
                <Label>Date</Label>
                <DatePickerField
                  value={editingCredit.date}
                  onChange={(v) => setEditingCredit({ ...editingCredit, date: v })}
                  className="mt-1"
                  testId="edit-credit-date"
                />
              </div>
              <div>
                <Label>Annonceur / Client</Label>
                <Input
                  value={editingCredit.annonceur || ''}
                  onChange={(e) => setEditingCredit({ ...editingCredit, annonceur: e.target.value })}
                  className="mt-1"
                  data-testid="edit-credit-annonceur"
                  placeholder="Ex: Orange, Ecobank, MTN..."
                />
              </div>
              <div>
                <Label>Description *</Label>
                <Textarea
                  value={editingCredit.description}
                  onChange={(e) => setEditingCredit({ ...editingCredit, description: e.target.value })}
                  rows={2}
                  className="mt-1"
                  data-testid="edit-credit-description"
                />
              </div>
              <div>
                <Label>Montant (FCFA) *</Label>
                <Input
                  type="number"
                  value={editingCredit.montant}
                  onChange={(e) => setEditingCredit({ ...editingCredit, montant: parseFloat(e.target.value) || 0 })}
                  className="mt-1"
                  data-testid="edit-credit-montant"
                />
              </div>
              <div className="flex justify-end gap-2 pt-4 border-t">
                <Button variant="outline" onClick={() => setShowEditCreditModal(false)}>Annuler</Button>
                <Button
                  onClick={handleSaveEditCredit}
                  className="bg-blue-600 hover:bg-blue-700 gap-2"
                  data-testid="save-edit-credit-btn"
                >
                  <Save className="h-4 w-4" />
                  Enregistrer les corrections
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ===== Modal DISTRIBUTION POOL 20% ===== */}
      <Dialog open={showDistributePoolModal} onOpenChange={setShowDistributePoolModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-purple-700">
              <Users className="h-5 w-5" />
              Distribuer le pool 20% en dividendes
            </DialogTitle>
          </DialogHeader>
          <div className="py-2 space-y-3">
            <p className="text-sm text-gray-600">
              Les charges suivantes seront créées automatiquement (catégorie <strong>Salaires</strong>, statut <em>En attente</em>) pour chaque investisseur actif au prorata de sa mise :
            </p>
            <div className="border border-purple-200 rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-purple-50 text-purple-900">
                  <tr>
                    <th className="px-3 py-2 text-left font-semibold">Investisseur</th>
                    <th className="px-3 py-2 text-right font-semibold">Mise</th>
                    <th className="px-3 py-2 text-right font-semibold">Part du pool</th>
                    <th className="px-3 py-2 text-right font-semibold">Dividende (FCFA)</th>
                  </tr>
                </thead>
                <tbody>
                  {distributionPreview.map(p => (
                    <tr key={p.id} className="border-b hover:bg-purple-50/50">
                      <td className="px-3 py-2 font-medium">{p.fullName}</td>
                      <td className="px-3 py-2 text-right text-gray-700">
                        {(parseFloat(p.mise) || 0).toLocaleString('fr-FR')} FCFA
                      </td>
                      <td className="px-3 py-2 text-right text-purple-700">
                        {(p.share * 100).toFixed(2)}%
                      </td>
                      <td className="px-3 py-2 text-right font-bold text-purple-800">
                        {p.montant.toLocaleString('fr-FR')} FCFA
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-purple-100 font-bold">
                  <tr>
                    <td colSpan="3" className="px-3 py-2 text-right text-purple-900">TOTAL DISTRIBUÉ :</td>
                    <td className="px-3 py-2 text-right text-purple-900">
                      {distributionPreview.reduce((s, p) => s + p.montant, 0).toLocaleString('fr-FR')} FCFA
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
            <p className="text-xs text-gray-500 italic">
              Vous pourrez ensuite modifier ou supprimer chaque ligne individuellement depuis le tableau des Charges.
            </p>
          </div>
          <DialogFooter className="gap-2 flex-row flex-wrap">
            <Button variant="outline" onClick={() => setShowDistributePoolModal(false)} className="flex-1 min-w-[120px]">
              Annuler
            </Button>
            <Button
              onClick={notifyInvestorsByEmail}
              disabled={notifying || distributionPreview.length === 0}
              variant="outline"
              className="flex-1 min-w-[180px] border-blue-300 text-blue-700 hover:bg-blue-50 gap-1"
              data-testid="notify-investors-btn"
              title="Envoyer un email à chaque investisseur avec son dividende"
            >
              <Mail className="h-4 w-4" /> {notifying ? 'Envoi...' : 'Notifier par email'}
            </Button>
            <Button
              onClick={confirmDistributePool}
              disabled={distributing}
              className="flex-1 min-w-[180px] bg-purple-600 hover:bg-purple-700"
              data-testid="confirm-distribute-pool-btn"
            >
              {distributing ? 'Distribution...' : 'Confirmer la distribution'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ===== Modal CONFIRMATION DE SUPPRESSION ===== */}
      <Dialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <Trash2 className="h-5 w-5" />
              Confirmer la suppression
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-gray-700">
              Voulez-vous vraiment supprimer ce {deleteTarget?.type === 'debit' ? 'débit' : 'crédit'} ?
            </p>
            {deleteTarget?.description && (
              <p className="mt-2 p-3 bg-gray-50 rounded border-l-4 border-gray-400 text-sm italic">
                « {deleteTarget.description} »
              </p>
            )}
            <p className="text-xs text-gray-500 mt-3">
              Le Compte de Résultat sera recalculé automatiquement. Cette action est irréversible.
            </p>
          </div>
          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button
              variant="outline"
              type="button"
              onClick={() => setDeleteTarget(null)}
              data-testid="cancel-delete-btn"
            >
              Annuler
            </Button>
            <Button
              type="button"
              onClick={confirmDelete}
              className="bg-red-600 hover:bg-red-700 gap-2"
              data-testid="confirm-delete-btn"
            >
              <Trash2 className="h-4 w-4" />
              Supprimer
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Company Info Sheet */}
      <EditCompanyInfoSheet
        open={showEditCompany}
        onOpenChange={setShowEditCompany}
        companyInfo={companyInfo}
        onSaved={(updated) => { setCompanyInfo({ ...(companyInfo || {}), ...updated }); loadCompanyInfo(); loadAnalytics(); }}
      />
    </Layout>
  );
}
